import CompanyOptionLabel from "../CompanyOptionLabel";
import CompanyTableCell from "../CompanyTableCell";
//AddCandidateDialog.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  LinearProgress,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  InputAdornment,
  Avatar,
  Autocomplete,
  TableCell,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  dedupeAutocompleteOptions,
  normalizeAutocompleteText,
} from "../../utils/autocompleteOptions";

import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";

import CandidateEntryOptions from "./addCandidate/CandidateEntryOptions";
import UploadCandidateStep from "./addCandidate/UploadCandidateStep";
import UseLinkCandidateStep from "./addCandidate/UseLinkCandidateStep";
import StartFromScratchCandidateStep from "./addCandidate/StartFromScratchCandidateStep";
import {
  createTeamMemberIfMissing,
  fetchTeamMembers,
  normalizeTeamMemberName,
  upsertTeamMemberOption,
} from "../../utils/teamMembers";
import { fetchCompanyContactOptions } from "../../utils/hiringManagers";
import {
  buildCompanySocialPayloadFromLinks,
  getPrimaryCompanyWebsite,
  isCompanySocialUrl,
} from "../../utils/companySocialLinks";
import { deriveLinkAutofill, describeLinkAutofill } from "../../utils/linkAutofill";
import {
  normalizeLocationValueForSave,
  normalizeLocationValuesForSave,
} from "../../utils/locationFilters";
import LocationMultiSelectField from "../LocationMultiSelectField";
import GenericDataTable from "../GenericDataTable";
import {
  pillButtonBaseSx,
  pillButtonInteractiveSx,
  popupNavigationPillSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";
import { formatMissingRequiredFieldsMessage } from "../../utils/requiredFieldMessages";
import { CANDIDATE_STATUS_OPTIONS } from "../../utils/candidateStatusOptions";
import {
  clearCandidateImportWork,
  writeCandidateImportWork,
} from "../../utils/candidateImportWorkState";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const OVERVIEW_DRAFT_KEY = "rpo:add-candidate-overview-draft";

try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString().replace(/\.mjs$/, ".js");
} catch {}

const SOURCE_OPTIONS = ["Referral", "LinkedIn", "Job board", "Website", "Other"];

function getExtractedResumeCompanies(resumeData = {}) {
  const names = [
    ...(Array.isArray(resumeData?.experience_items) ? resumeData.experience_items : []).map(
      (item) => item?.company
    ),
    ...(Array.isArray(resumeData?.past_companies) ? resumeData.past_companies : []),
  ];
  const seen = new Set();
  return names.filter((value) => {
    const name = String(value || "").trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
const WORK_AUTH_OPTIONS = ["US Citizen", "Green Card", "H1B", "TN", "OPT", "CPT", "Other"];
const PREFERRED_JOB_TYPE_OPTIONS = [
  "Full-time",
  "Part-time",
  "Contract",
  "On-site",
  "Hybrid",
  "Remote",
];
const AVAILABILITY_OPTIONS = [];
const JOB_MATCH_STAGE_OPTIONS = [
  { value: "New", label: "New" },
  { value: "Applied", label: "Applied" },
  { value: "Matched", label: "Matched" },
  { value: "Sourced", label: "Sourced" },
  { value: "Contacted", label: "Contacted" },
  { value: "Screened", label: "Screened" },
  { value: "Presented", label: "Presented" },
  { value: "Interview", label: "Interview" },
  { value: "Assessment", label: "Assessment" },
  { value: "Offer", label: "Offer" },
  { value: "Hired", label: "Hired" },
  { value: "Rejected", label: "Rejected" },
];

const DEFAULT_FORM = {
  full_name: "",
  email: "",
  phone: "",
  title: "",
  location: "",
  address: "",
  owner: "",
  status: "New",
  stage: "New",
  job_id: "",

  resume_url: "",
  linkedin_url: "",
  skills: "",
  work_authorization: "",
  work_auhtorization: "",
  notes: "",
  source: "LinkedIn",

  website: "",
  website_url: "",
  other_url: "",

  company_client: "",
  company_client_id: null,
  company_client_logo: "",

  assigned_team_member: "",
  team_name: "",

  extra_emails: "",
  extra_phones: "",
  extra_websites: "",
  why_looking: "",
  job_types: "",
  work_modes: "",
  timeline: "",
  compensation: "",
  interview_availability: "",
  preferred_job_type: "",
  preferred_work_mode: "",
  salary_range: "",
  availability: "",
  preferred_locations: "",
};

const EMPTY_RESUME_AI = {
  experience_items: [],
  education_items: [],
  company_client: "",
  current_company: "",
  company_website: "",
  company_link: "",
  career_site: "",
  company_location: "",
  company_description: "",
  full_company_description: "",
  elevator_pitch: "",
  mission_and_vision: "",
  key_products_services: "",
  unique_selling_points: "",
  employee_perks: "",
  achievements_awards: "",
  industry: "",
  industry_sector: "",
  size_range: "",
  company_size: "",
  founded_year: "",
  ownership_type: "",
  parent_company: "",
  work_policies: "",
  recruitment_tools: [],
  job_boards: [],
  job_board_rating: "",
  company_email: "",
  company_phone: "",
  linkedin_url: "",
  facebook_url: "",
  twitter_url: "",
  instagram_url: "",
  youtube_url: "",
  tiktok_url: "",
};

const RESUME_AI_TEXT_FIELDS = [
  "company_client",
  "current_company",
  "company_website",
  "company_link",
  "career_site",
  "company_location",
  "company_description",
  "full_company_description",
  "elevator_pitch",
  "mission_and_vision",
  "key_products_services",
  "unique_selling_points",
  "employee_perks",
  "achievements_awards",
  "industry",
  "industry_sector",
  "size_range",
  "company_size",
  "founded_year",
  "ownership_type",
  "parent_company",
  "work_policies",
  "job_board_rating",
  "company_email",
  "company_phone",
  "linkedin_url",
  "facebook_url",
  "twitter_url",
  "instagram_url",
  "youtube_url",
  "tiktok_url",
];

const RESUME_AI_LIST_FIELDS = ["recruitment_tools", "job_boards"];

const COMPANY_PROFILE_CHECK_FIELDS = [
  "company_website",
  "company_link",
  "career_site",
  "company_location",
  "company_description",
  "full_company_description",
  "elevator_pitch",
  "mission_and_vision",
  "key_products_services",
  "unique_selling_points",
  "employee_perks",
  "achievements_awards",
  "industry",
  "industry_sector",
  "size_range",
  "company_size",
  "founded_year",
  "ownership_type",
  "parent_company",
  "work_policies",
  "company_email",
  "company_phone",
  "job_board_rating",
  "linkedin_url",
  "facebook_url",
  "twitter_url",
  "instagram_url",
  "youtube_url",
  "tiktok_url",
];

const PURPLE = "#7C3AED";
const PURPLE_HOVER = "#6D28D9";
const SOFT_BORDER = "#EAE5FF";
const LABEL = "#6B7280";
const LINK_BACKGROUND_MESSAGE =
  "Candidate added. We're still finishing the link import in the background.";
const COMPANY_TABLE_VIEW_MESSAGE =
  "If a new company is needed, it will be added to the Companies table view.";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    bgcolor: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: SOFT_BORDER,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D7CBFF",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PURPLE,
    borderWidth: 2,
  },
};

const textareaFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    borderRadius: 5,
  },
};

const companyFilter = createFilterOptions();
const teamMemberFilter = createFilterOptions();
const DIALOG_SCREEN_VALUES = new Set([
  "start",
  "upload",
  "link",
  "overview",
  "professional",
  "additional",
]);

const normalizeCandidateDialogScreen = (value, fallback = "start") => {
  const normalized = String(value || "").trim().toLowerCase();
  return DIALOG_SCREEN_VALUES.has(normalized) ? normalized : fallback;
};

function LabelText({ children }) {
  return (
    <Typography data-popup-label sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1, color: LABEL, mb: 0.6 }}>
      {children}
    </Typography>
  );
}

function Section({ children }) {
  return (
    <Typography data-popup-section sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: PURPLE, letterSpacing: 0.2 }}>
      {children}
    </Typography>
  );
}

function PillButton({ sx, ...props }) {
  return (
    <Button
      {...props}
      className={withPillButtonClassName(props.className)}
      sx={[pillButtonBaseSx, pillButtonInteractiveSx, { fontWeight: 400, px: 2.1 }, sx || {}]}
    />
  );
}

const popupFooterScrollSx = {
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
};

const popupFooterRowSx = {
  width: "max-content",
  minWidth: "100%",
  flexWrap: "nowrap",
};

const popupFooterActionGroupSx = {
  flexWrap: "nowrap",
};

const popupFooterButtonSx = {
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const Card = React.memo(function Card({ title, children, onClose, saving }) {
  return (
    <Box data-popup-surface
      sx={{
        width: "100%",
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 3,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 4, pt: 4, pb: 2 }}>
        <Stack data-popup-header direction="row" alignItems="center" spacing={1}>
          <PersonOutlineIcon sx={{ fontSize: 26, color: "#3451F4" }} />

          <Typography data-popup-title sx={{ fontSize: 20, fontWeight: 400, lineHeight: "35px", color: "#111827" }}>
            {title}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <IconButton data-popup-close
            onClick={onClose}
            disabled={saving}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 999,
              bgcolor: PURPLE,
              color: "#fff",
              "&:hover": {
                bgcolor: PURPLE_HOVER,
              },
            }}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 15, color: "#fff" }} />
          </IconButton>
        </Stack>
      </Box>
      {children}
    </Box>
  );
});

const Footer = React.memo(function Footer({
  leftText,
  onLeft,
  rightText,
  onRight,
  rightVariant = "contained",
  saving,
  parsingResume,
}) {
  const leftIsBack = String(leftText || "").trim().toLowerCase() === "back";
  const rightIsNavigation = ["next", "continue"].includes(
    String(rightText || "").trim().toLowerCase()
  );

  return (
    <Box data-popup-footer sx={{ px: 4, pb: 4, pt: 2, ...popupFooterScrollSx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={popupFooterRowSx}>
        <PillButton
          variant={leftIsBack ? "contained" : "outlined"}
          onClick={onLeft}
          disabled={saving || parsingResume}
          startIcon={leftIsBack ? <ArrowBackRoundedIcon /> : null}
          sx={{
            ...popupFooterButtonSx,
            borderColor: leftIsBack ? PURPLE : SOFT_BORDER,
            color: leftIsBack ? "#fff" : PURPLE,
            bgcolor: leftIsBack ? PURPLE : "#fff",
            "&:hover": {
              bgcolor: leftIsBack ? PURPLE_HOVER : "#fff",
            },
            ...(leftIsBack ? popupNavigationPillSx : {}),
          }}
        >
          {leftText}
        </PillButton>

        <Box sx={{ flex: 1 }} />

        <PillButton
          variant={rightVariant}
          onClick={onRight}
          disabled={saving || parsingResume}
          endIcon={rightIsNavigation ? <ArrowForwardRoundedIcon /> : null}
          sx={{
            ...popupFooterButtonSx,
            bgcolor: rightVariant === "contained" ? PURPLE : "#fff",
            color: rightVariant === "contained" ? "#fff" : PURPLE,
            borderColor: SOFT_BORDER,
            "&:hover": { bgcolor: rightVariant === "contained" ? PURPLE_HOVER : "#fff" },
            minWidth: 110,
            ...(rightIsNavigation ? popupNavigationPillSx : {}),
          }}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {rightText}
        </PillButton>
      </Stack>
    </Box>
  );
});

const OverviewFooter = React.memo(function OverviewFooter({
  onBack,
  onNext,
  saving,
  parsingResume,
}) {
  const disabled = saving || parsingResume;

  return (
    <Box data-popup-footer sx={{ px: 4, pb: 4, pt: 2, ...popupFooterScrollSx }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={popupFooterRowSx}
      >
        <Button
          className={withPillButtonClassName()}
          variant="contained"
          onClick={onBack}
          disabled={disabled}
          startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            minWidth: 78,
            height: 30,
            borderRadius: 999,
            textTransform: "none",
            backgroundColor: PURPLE,
            color: "#fff",
            fontWeight: 500,
            ...popupFooterButtonSx,
            "&:hover": {
              backgroundColor: PURPLE_HOVER,
            },
            ...popupNavigationPillSx,
          }}
        >
          Back
        </Button>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={popupFooterActionGroupSx}
        >
          <Button
            className={withPillButtonClassName()}
            variant="contained"
            onClick={onNext}
            disabled={disabled}
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              minWidth: 80,
              height: 30,
              borderRadius: 999,
              textTransform: "none",
              backgroundColor: PURPLE,
              color: "#fff",
              fontWeight: 500,
              ...popupFooterButtonSx,
              "&:hover": {
                backgroundColor: PURPLE_HOVER,
              },
              ...popupNavigationPillSx,
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
});

function safeDecodeJwt(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function safeUrl(input) {
  const v = (input || "").trim();
  if (!v) return "";
  try {
    new URL(v.startsWith("http") ? v : `https://${v}`);
    return v.startsWith("http") ? v : `https://${v}`;
  } catch {
    return null;
  }
}

const CANDIDATE_RESUME_LINK_EXTENSIONS = new Set([".pdf", ".doc", ".docx"]);
const COMMON_EMAIL_WEBSITE_HOSTS = new Set([
  "aol.com",
  "gmail.com",
  "googlemail.com",
  "hotmail.com",
  "icloud.com",
  "live.com",
  "me.com",
  "msn.com",
  "outlook.com",
  "proton.me",
  "protonmail.com",
  "yahoo.com",
  "yandex.com",
  "zoho.com",
]);
function isLikelyResumeDocumentUrl(input) {
  const normalized = safeUrl(input);
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    const extension = String(parsed.pathname || "")
      .toLowerCase()
      .match(/\.(pdf|doc|docx)$/i)?.[0];
    return Boolean(extension && CANDIDATE_RESUME_LINK_EXTENSIONS.has(extension));
  } catch {
    return false;
  }
}

function isResumeDocumentLinkKind(value) {
  return String(value || "").trim().toLowerCase() === "resume_document";
}

function getCandidateLinkAutofill(value = "") {
  const autofill = deriveLinkAutofill(value, "candidate");
  const isResumeDocument = isLikelyResumeDocumentUrl(autofill.normalizedUrl || value);

  if (!isResumeDocument) {
    return {
      ...autofill,
      isResumeDocument: false,
      documentHostWebsite: "",
      documentHostCompanyName: "",
    };
  }

  return {
    ...autofill,
    companyName: "",
    website: "",
    isResumeDocument: true,
    documentHostWebsite: autofill.website || "",
    documentHostCompanyName: autofill.companyName || "",
  };
}

function normalizeText(t) {
  return String(t || "")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function toLines(text) {
  return normalizeText(text)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

function stripPunct(s) {
  return String(s || "")
    // Preserve compound surnames when the source document uses a Unicode dash
    // (including the non-breaking hyphen commonly emitted by resume exports).
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[^\p{L}\p{N}\s.'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isLikelyHeaderNoise(line) {
  return /resume|curriculum vitae|cv\b|page\s*\d+|references available/i.test(line);
}

function isSectionHeader(line) {
  return /^(summary|professional summary|experience|work experience|employment|education|(?:technical|core|key|professional|relevant) skills|skills|projects|certifications|objective|profile)\b/i.test(
    String(line || "").trim()
  );
}

function splitOnPipesOrBullets(line) {
  return String(line || "")
    .split(/[|•·▪●]+/g)
    .map((p) => p.trim())
    .filter(Boolean);
}

function normalizeUrlToken(value) {
  let next = String(value || "")
    .trim()
    .replace(/^[("'[\s]+/, "")
    .replace(/[)"'\],;:.]+$/g, "")
    .trim();

  if (!next) return "";
  if (!/^https?:\/\//i.test(next)) next = `https://${next.replace(/^\/+/, "")}`;
  return safeUrl(next) || "";
}

function normalizeForEmailDetect(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s*\(at\)\s*/g, "@")
    .replace(/\s*\[at\]\s*/g, "@")
    .replace(/\s+at\s+/g, "@")
    .replace(/\s*\(dot\)\s*/g, ".")
    .replace(/\s*\[dot\]\s*/g, ".")
    .replace(/\s+dot\s+/g, ".")
    .replace(/\s*@\s*/g, "@")
    .replace(/\s*\.\s*/g, ".")
    .trim();
}

function isEmailLike(line) {
  const t = normalizeForEmailDetect(line);
  if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(t)) return true;
  if (
    /\b[a-z0-9._%+-]{2,}\s*[a-z0-9.-]{2,}\.(com|net|org|edu|gov|io|co|us|mx|ca|uk)\b/i.test(t)
  ) {
    return true;
  }
  return false;
}

function looksLikeContactLine(line) {
  const l = String(line || "").toLowerCase();
  if (isEmailLike(l)) return true;
  return (
    l.includes("linkedin.com") ||
    l.includes("github.com") ||
    l.includes("http://") ||
    l.includes("https://") ||
    /\b(email|e-mail|phone|mobile|cell)\b/.test(l)
  );
}

function extractEmail(text) {
  const t = normalizeForEmailDetect(text);
  const m = t.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  return m ? m[0] : "";
}

function pickPhoneFromText(text) {
  const t = String(text || "");
  const patterns = [
    /(\+\d{1,3}[\s.-]?)?(\(?\d{2,4}\)?[\s.-]?)\d{2,4}[\s.-]?\d{2,4}[\s.-]?\d{0,4}/g,
    /(\+\d{1,3}[\s.-]?)?(\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/g,
  ];

  const hits = [];
  for (const re of patterns) {
    let m;
    while ((m = re.exec(t)) !== null) {
      const raw = String(m[0] || "").trim();
      const digits = raw.replace(/\D/g, "");
      if (digits.length >= 10 && digits.length <= 15) hits.push(raw);
      if (hits.length > 6) break;
    }
    if (hits.length) break;
  }

  if (!hits.length) return "";
  hits.sort((a, b) => {
    const da = a.replace(/\D/g, "").length;
    const db = b.replace(/\D/g, "").length;
    return Math.abs(da - 10) - Math.abs(db - 10);
  });

  return hits[0];
}

function extractPhone(lines, text) {
  const labelRe = /(phone|mobile|cell)\s*[:-]\s*(.+)/i;
  for (const line of lines.slice(0, 80)) {
    const lm = line.match(labelRe);
    if (lm && lm[2]) {
      const p = pickPhoneFromText(lm[2]);
      if (p) return p;
    }
  }
  return pickPhoneFromText(text);
}

function extractLinkedIn(text) {
  const m =
    String(text || "").match(/https?:\/\/(www\.)?linkedin\.com\/[^\s)]+/i) ||
    String(text || "").match(/(www\.)?linkedin\.com\/[^\s)]+/i);
  if (!m) return "";
  const url = m[0].startsWith("http") ? m[0] : `https://${m[0]}`;
  return safeUrl(url) || "";
}

function extractWebsite(text) {
  const raw = String(text || "");
  const labeledMatch =
    raw.match(/(?:portfolio|website|site)\s*:\s*([^\s|]+)/i)?.[1] || "";
  const labeledUrl = normalizeUrlToken(labeledMatch);
  if (labeledUrl) {
    try {
      const labeledHost = new URL(labeledUrl).hostname.replace(/^www\./i, "").toLowerCase();
      if (
        !/linkedin\.com|github\.com/i.test(labeledHost) &&
        !COMMON_EMAIL_WEBSITE_HOSTS.has(labeledHost)
      ) {
        return labeledUrl;
      }
    } catch {}
  }

  const matches = raw.match(/(?:https?:\/\/|www\.)[^\s|)]+/gi) || [];
  for (const match of matches) {
    const normalized = normalizeUrlToken(match);
    if (!normalized) continue;

    try {
      const host = new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
      if (/linkedin\.com|github\.com/i.test(host)) continue;
      if (COMMON_EMAIL_WEBSITE_HOSTS.has(host)) continue;
      return normalized;
    } catch {}
  }

  return "";
}

function extractLabeledValue(lines, labelRegex) {
  for (const line of lines.slice(0, 160)) {
    const m = line.match(labelRegex);
    if (m && m[1]) return m[1].trim();
  }
  return "";
}

function extractLocation(lines, text) {
  const labeled = extractLabeledValue(lines, /^(location|address)\s*[:-]\s*(.+)$/i);
  if (labeled && labeled.length <= 80) return labeled;

  const remote = String(text || "").match(/\b(Remote|Hybrid)\b/i);
  if (remote) return remote[0];

  for (const line of lines.slice(0, 90)) {
    const m = line.match(/\b([A-Z][a-zA-Z]+(?:[\s-][A-Z][a-zA-Z]+){0,3}),\s*([A-Z]{2})\b/);
    if (m) return `${m[1]}, ${m[2]}`;
  }

  for (const line of lines.slice(0, 90)) {
    const m = line.match(/\b([A-Z][a-zA-Z]+(?:[\s-][A-Z][a-zA-Z]+){0,3})\s+([A-Z]{2})\b/);
    if (m) return `${m[1]}, ${m[2]}`;
  }

  return "";
}

const ROLE_KEYWORDS_RE =
  /\b(engineer|developer|designer|architect|manager|product|data|analyst|qa|devops|full[-\s]?stack|frontend|back[-\s]?end|software|consultant|administrator|lead|director|recruiter|specialist|sre|principal|staff)\b/i;
const NAME_COMPANY_HINT_WORDS = new Set([
  "agency",
  "associates",
  "capital",
  "collective",
  "company",
  "consulting",
  "corp",
  "corporation",
  "digital",
  "gmbh",
  "group",
  "holdings",
  "inc",
  "incorporated",
  "lab",
  "labs",
  "llc",
  "ltd",
  "partners",
  "plc",
  "sa",
  "services",
  "sl",
  "solutions",
  "studio",
  "studios",
  "systems",
  "tech",
  "technologies",
  "technology",
  "ventures",
  "works",
]);

function looksLikeJobTitle(s) {
  return ROLE_KEYWORDS_RE.test(String(s || ""));
}

function countCompanyNameHintWords(value) {
  return stripPunct(value)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .reduce((count, word) => {
      const normalized = word.replace(/[.'-]/g, "");
      return count + (NAME_COMPANY_HINT_WORDS.has(normalized) ? 1 : 0);
    }, 0);
}

function looksLikeCompanyName(value) {
  return countCompanyNameHintWords(value) > 0;
}

function normalizeExtractedFullName(value) {
  return stripPunct(String(value || "")).replace(/\s+/g, " ").trim();
}

function scoreNameCandidate(line) {
  const raw = String(line || "");
  const l = stripPunct(raw);
  if (!l) return -999;

  if (isLikelyHeaderNoise(l)) return -999;
  if (isSectionHeader(l)) return -999;
  if (looksLikeContactLine(raw) || isEmailLike(raw)) return -999;

  if (looksLikeJobTitle(l)) return -40;
  if (/\d/.test(l)) return -50;

  const words = l.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 4) return -40;

  let alphaWords = 0;
  let titleCaseWords = 0;
  let allCapsWords = 0;
  let companyHintWords = 0;

  for (const w of words) {
    if (/^[A-Za-zÀ-ÖØ-öø-ÿ.'-]+$/.test(w)) alphaWords++;
    if (/^[A-Z][a-zÀ-ÖØ-öø-ÿ.'-]+$/.test(w)) titleCaseWords++;
    if (/^[A-ZÀ-ÖØ-Þ.'-]{2,}$/.test(w)) allCapsWords++;
    if (NAME_COMPANY_HINT_WORDS.has(w.toLowerCase().replace(/[.'-]/g, ""))) companyHintWords++;
  }

  let score = 0;
  score += alphaWords * 2;
  score += titleCaseWords * 3;
  score += allCapsWords * 2;

  if (l.length > 60) score -= 10;
  if (words.length === 2) score += 6;
  if (words.length === 3) score += 5;
  if (companyHintWords) score -= companyHintWords * 12;

  return score;
}

function shouldReplaceCandidateFullName(currentValue, nextValue) {
  const current = normalizeExtractedFullName(currentValue);
  const next = normalizeExtractedFullName(nextValue);

  if (!next) return false;
  if (!current) return scoreNameCandidate(next) >= 8 && !looksLikeCompanyName(next);
  if (current.toLowerCase() === next.toLowerCase()) return false;

  const currentScore = scoreNameCandidate(current);
  const nextScore = scoreNameCandidate(next);
  const currentLooksCompany = looksLikeCompanyName(current);
  const nextLooksCompany = looksLikeCompanyName(next);
  const currentWords = current.split(/\s+/).filter(Boolean).length;
  const nextWords = next.split(/\s+/).filter(Boolean).length;

  if (nextLooksCompany && !currentLooksCompany) return false;
  if (currentLooksCompany && !nextLooksCompany) return true;
  if (nextWords >= 2 && currentWords < 2 && nextScore >= 8) return true;
  if (nextScore >= 8 && currentScore < 8) return true;
  if (nextScore >= currentScore + 3) return true;

  return false;
}

function isStrongCandidateFullName(value) {
  const normalized = normalizeExtractedFullName(value);
  if (!normalized) return false;
  return scoreNameCandidate(normalized) >= 8 && !looksLikeCompanyName(normalized);
}

function extractFullName(lines) {
  for (let i = 0; i < Math.min(lines.length, 40); i++) {
    const line = lines[i];
    const m = line.match(/^(name)\s*[:-]\s*(.+)$/i);
    if (m && m[2]) {
      const v = stripPunct(m[2]);
      if (v.split(/\s+/).length >= 2 && !isEmailLike(v) && !looksLikeJobTitle(v)) {
        return { full_name: v, nameLineIndex: i };
      }
    }
  }

  for (let i = 0; i < Math.min(lines.length, 5); i++) {
    const first = stripPunct(lines[i]);
    if (!first) continue;
    const s = scoreNameCandidate(first);
    if (s >= 8) return { full_name: first, nameLineIndex: i };
  }

  for (let i = 0; i < Math.min(lines.length, 20); i++) {
    const line = lines[i];
    if (!line.includes("|")) continue;
    const parts = splitOnPipesOrBullets(line);
    const first = stripPunct(parts[0] || "");
    const s = scoreNameCandidate(first);
    if (s >= 8) return { full_name: first, nameLineIndex: i };
  }

  const top = lines.slice(0, 80);
  let best = "";
  let bestScore = -999;
  let bestIdx = -1;

  for (let i = 0; i < top.length; i++) {
    const line = top[i];
    const candidate = line.includes("|") ? splitOnPipesOrBullets(line)[0] || line : line;
    const s = scoreNameCandidate(candidate) - i * 0.35;
    if (s > bestScore) {
      bestScore = s;
      best = stripPunct(candidate);
      bestIdx = i;
    }
  }

  return { full_name: bestScore >= 7 ? best : "", nameLineIndex: bestIdx };
}

function cleanExtractedTitle(title) {
  // Remove common separators and company references
  // e.g., "Senior Engineer at Microsoft" -> "Senior Engineer"
  // "John Doe - Software Developer" -> "Software Developer"
  let cleaned = String(title || "").trim();
  
  // Remove company name after " at ", " @ ", " | ", " - ", " / "
  cleaned = cleaned
    .replace(/\s+at\s+.*/i, "")           // "at Microsoft" -> ""
    .replace(/\s*@\s*.*/i, "")             // "@ Microsoft" -> ""
    .replace(/\s*\|\s*.*/i, "")            // "| Microsoft" -> ""
    .replace(/\s*-\s+.*/i, "")             // "- Company" -> "" (only if preceded by space)
    .replace(/\s*\/\s*.*/i, "")            // "/ Department" -> ""
    .trim();
  
  return cleaned;
}

function scoreTitleLine(line) {
  const l = String(line || "").trim();
  if (!l) return -999;
  if (isLikelyHeaderNoise(l)) return -50;
  if (isSectionHeader(l)) return -60;

  const hasContact = looksLikeContactLine(l);
  if (l.length > 90) return -10;

  let score = 0;
  const hasRoleKw = ROLE_KEYWORDS_RE.test(l);
  if (hasRoleKw) score += 16;
  if (hasContact && !hasRoleKw) score -= 12;

  const words = l.split(/\s+/).length;
  if (words >= 2 && words <= 12) score += 3;
  if (/\d/.test(l)) score -= 2;

  return score;
}

function extractTitle(lines, nameLineIndex) {
  const labeled = extractLabeledValue(lines, /^(title|role|position|headline)\s*[:-]\s*(.+)$/i);
  if (labeled && labeled.length <= 90) return cleanExtractedTitle(labeled);

  // Helper to reject a line that looks like a person's name (not a job title)
  const looksLikeName = (line) => {
    const s = String(line || "").trim();
    return !looksLikeJobTitle(s) && scoreNameCandidate(s) >= 7;
  };

  if (typeof nameLineIndex === "number" && nameLineIndex >= 0) {
    const windowLines = lines.slice(nameLineIndex + 1, nameLineIndex + 7);
    let best = "";
    let bestScore = -999;
    for (const line of windowLines) {
      if (looksLikeName(line)) continue;
      const s = scoreTitleLine(line);
      if (s > bestScore) {
        bestScore = s;
        best = line.trim();
      }
    }
    if (bestScore >= 8) return cleanExtractedTitle(best);
  }

  const top = lines.slice(0, 100);
  let best = "";
  let bestScore = -999;
  for (const line of top) {
    if (looksLikeName(line)) continue;
    const s = scoreTitleLine(line);
    if (s > bestScore) {
      bestScore = s;
      best = line.trim();
    }
  }

  return bestScore >= 10 ? cleanExtractedTitle(best) : "";
}

function sliceSection(lines, headerRegex) {
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    if (headerRegex.test(lines[i])) {
      start = i;
      break;
    }
  }
  if (start === -1) return [];

  const out = [];
  for (let i = start + 1; i < Math.min(lines.length, start + 120); i++) {
    const line = lines[i];
    if (isSectionHeader(line)) break;
    out.push(line);
  }
  return out;
}

function cleanSkillsTokens(tokens) {
  const cleaned = [];
  const seen = new Set();

  for (let t of tokens) {
    t = t.trim();
    if (!t) continue;
    t = t.replace(/[•·▪●]/g, "").trim();
    t = t.replace(/\s+/g, " ");
    if (t.length < 2 || t.length > 40) continue;

    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(t);
  }

  return cleaned;
}

const TECH_KEYWORDS = [
  "React",
  "Next.js",
  "Node",
  "Express",
  "JavaScript",
  "TypeScript",
  "HTML",
  "CSS",
  "Tailwind",
  "Material UI",
  "MUI",
  "Redux",
  "REST",
  "GraphQL",
  "SQL",
  "MySQL",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "Docker",
  "Kubernetes",
  "AWS",
  "GCP",
  "Azure",
  "CI/CD",
  "Git",
  "Jest",
  "Playwright",
  "Cypress",
  "Python",
  "Java",
  "C#",
  "PHP",
  "WordPress",
  "WooCommerce",
];

function mineSkillsFromText(text) {
  const found = [];
  const lower = String(text || "").toLowerCase();
  for (const kw of TECH_KEYWORDS) {
    if (lower.includes(kw.toLowerCase())) found.push(kw);
  }
  return cleanSkillsTokens(found);
}

function extractSkills(lines, text) {
  const section = sliceSection(
    lines,
    /^(skills|technical skills|core skills|technologies|tech stack|tools)\b/i
  );

  let tokens = [];
  if (section.length) {
    const joined = section.join(" | ");
    tokens = joined
      .split(/[,|•·▪●/]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const mined = mineSkillsFromText(text);
  return cleanSkillsTokens([...tokens, ...mined]).slice(0, 25);
}

function extractCandidateFromText(rawText) {
  const text = normalizeText(rawText);
  const lines = toLines(text);

  const { full_name, nameLineIndex } = extractFullName(lines);
  const email = extractEmail(text);
  const phone = extractPhone(lines, text);
  const linkedin_url = extractLinkedIn(text);
  const website_url = extractWebsite(text);
  const location = extractLocation(lines, text);
  const title = extractTitle(lines, nameLineIndex);
  const skills = extractSkills(lines, text);

  return {
    full_name,
    title,
    email,
    phone,
    linkedin_url,
    website_url,
    website: website_url,
    location,
    skills,
  };
}

async function readFileAsArrayBuffer(file) {
  return await file.arrayBuffer();
}

function detectTwoColumns(items) {
  const xs = items.map((it) => it.x).filter((v) => Number.isFinite(v));
  if (xs.length < 40) return null;

  xs.sort((a, b) => a - b);
  const minX = xs[0];
  const maxX = xs[xs.length - 1];
  const width = maxX - minX;
  if (width < 200) return null;

  let bestGap = 0;
  let splitX = null;

  for (let i = 1; i < xs.length; i++) {
    const gap = xs[i] - xs[i - 1];
    const xMid = (xs[i] + xs[i - 1]) / 2;
    const inMiddle = xMid > minX + width * 0.25 && xMid < minX + width * 0.75;
    if (inMiddle && gap > bestGap) {
      bestGap = gap;
      splitX = xMid;
    }
  }

  if (bestGap >= 35 && splitX != null) return splitX;
  return null;
}

async function parsePdfToText(file) {
  const ab = await readFileAsArrayBuffer(file);
  const loadingTask = pdfjsLib.getDocument({ isEvalSupported: false, data: ab });
  const pdf = await loadingTask.promise;

  let fullText = "";

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();

    const items = (content.items || [])
      .map((it) => {
        const str = String(it.str || "").trim();
        const tx = it.transform || [];
        const x = tx[4] ?? 0;
        const y = tx[5] ?? 0;
        return { str, x, y };
      })
      .filter((it) => it.str);

    const splitX = detectTwoColumns(items);

    const columns = splitX
      ? { left: items.filter((it) => it.x < splitX), right: items.filter((it) => it.x >= splitX) }
      : { left: items, right: [] };

    const columnToLines = (colItems) => {
      if (!colItems.length) return [];
      colItems.sort((a, b) => b.y - a.y || a.x - b.x);

      const Y_TOL = 3.2;
      const lines = [];

      for (const it of colItems) {
        const last = lines[lines.length - 1];
        if (!last || Math.abs(last.y - it.y) > Y_TOL) lines.push({ y: it.y, parts: [it] });
        else last.parts.push(it);
      }

      return lines
        .map((ln) => {
          ln.parts.sort((a, b) => a.x - b.x);
          return ln.parts.map((p2) => p2.str).join(" ").replace(/\s+/g, " ").trim();
        })
        .filter(Boolean);
    };

    const leftLines = columnToLines(columns.left);
    const rightLines = columnToLines(columns.right);
    const pageLines = splitX ? [...leftLines, ...rightLines] : leftLines;

    fullText += pageLines.join("\n") + "\n\n";
  }

  return fullText;
}

async function extractPdfProfilePhoto(file) {
  const ab = await readFileAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ isEvalSupported: false, data: ab }).promise;
  const page = await pdf.getPage(1);
  const operators = await page.getOperatorList();
  const imageOperations = new Set([
    pdfjsLib.OPS.paintImageXObject,
    pdfjsLib.OPS.paintJpegXObject,
    pdfjsLib.OPS.paintInlineImageXObject,
  ]);
  const candidates = [];

  const getImage = (name) =>
    new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value || null);
      };

      try {
        const value = page.objs.get(name, finish);
        if (value) finish(value);
      } catch {
        finish(null);
      }
      window.setTimeout(() => finish(null), 1000);
    });

  for (let index = 0; index < operators.fnArray.length; index += 1) {
    if (!imageOperations.has(operators.fnArray[index])) continue;
    const imageName = operators.argsArray[index]?.[0];
    if (!imageName) continue;

    const image = await getImage(imageName);
    const width = Number(image?.width || 0);
    const height = Number(image?.height || 0);
    const pixels = width * height;
    const aspect = height ? width / height : 0;
    if (pixels < 2500 || pixels > 6000000 || aspect < 0.5 || aspect > 1.35) continue;

    candidates.push({ image, width, height, score: pixels * (aspect <= 1.1 ? 1.15 : 1) });
  }

  const selected = candidates.sort((left, right) => right.score - left.score)[0];
  const source = selected?.image;
  if (!source?.data || !selected) return null;

  const { width, height } = selected;
  const pixels = source.data;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return null;

  const output = context.createImageData(width, height);
  if (pixels.length === width * height * 4) {
    output.data.set(pixels);
  } else if (pixels.length === width * height * 3) {
    for (let sourceIndex = 0, outputIndex = 0; sourceIndex < pixels.length; sourceIndex += 3, outputIndex += 4) {
      output.data[outputIndex] = pixels[sourceIndex];
      output.data[outputIndex + 1] = pixels[sourceIndex + 1];
      output.data[outputIndex + 2] = pixels[sourceIndex + 2];
      output.data[outputIndex + 3] = 255;
    }
  } else {
    return null;
  }

  context.putImageData(output, 0, 0);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return blob ? new File([blob], "resume-profile-photo.png", { type: "image/png" }) : null;
}

function docxHtmlToText(html) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(html || ""), "text/html");
    if (!doc?.body) return "";

    const blocks = Array.from(doc.body.querySelectorAll("h1,h2,h3,h4,h5,h6,p,li,tr,td,th"));
    const lines = [];

    for (const el of blocks) {
      const t = String(el.textContent || "").replace(/\s+/g, " ").trim();
      if (!t) continue;

      if (el.tagName.toLowerCase() === "tr") {
        const cells = Array.from(el.querySelectorAll("td,th"))
          .map((c) => String(c.textContent || "").replace(/\s+/g, " ").trim())
          .filter(Boolean);
        if (cells.length) lines.push(cells.join(" | "));
        continue;
      }

      lines.push(t);
    }

    const out = [];
    for (const ln of lines) if (out[out.length - 1] !== ln) out.push(ln);

    return out.join("\n");
  } catch {
    return "";
  }
}

async function parseDocxToText(file) {
  const ab = await readFileAsArrayBuffer(file);

  try {
    const htmlRes = await mammoth.convertToHtml({ arrayBuffer: ab }, { externalFileAccess: false });
    const htmlText = docxHtmlToText(htmlRes?.value || "");
    if (htmlText && htmlText.length > 50) return htmlText;
  } catch {}

  const result = await mammoth.extractRawText({ arrayBuffer: ab }, { externalFileAccess: false });
  return result?.value || "";
}

function mergeAutofill(
  currentForm,
  extracted,
  { preferAiTitle = false, preferAiFields = false, preferExtractedFullName = false } = {}
) {
  const next = { ...currentForm };
  const extractedFullName = normalizeExtractedFullName(extracted?.full_name || "");
  const fillIfEmpty = (key, value) => {
    if (value == null) return;
    const valueString = Array.isArray(value) ? value.join(", ") : String(value || "").trim();
    if (!valueString) return;
    if (!String(next[key] || "").trim()) next[key] = valueString;
  };
  const fillAlways = (key, value) => {
    if (value == null) return;
    const valueString = Array.isArray(value) ? value.join(", ") : String(value || "").trim();
    if (!valueString) return;
    next[key] = valueString;
  };

  if (
    ((preferExtractedFullName || preferAiFields) && isStrongCandidateFullName(extractedFullName)) ||
    shouldReplaceCandidateFullName(next.full_name, extractedFullName)
  ) {
    next.full_name = extractedFullName;
  }
  const fillProfileField = preferAiFields ? fillAlways : fillIfEmpty;
  fillProfileField("email", extracted.email);
  fillProfileField("phone", extracted.phone);
  fillProfileField("linkedin_url", extracted.linkedin_url);
  fillProfileField("website_url", extracted.website_url);
  fillProfileField("website", extracted.website);
  const socialLinks = Array.isArray(extracted.social_links)
    ? extracted.social_links.map((value) => String(value || "").trim()).filter(Boolean)
    : [];
  const additionalLinks = socialLinks.filter((value) =>
    value !== extracted.linkedin_url && value !== extracted.website_url && value !== extracted.website
  );
  fillProfileField("other_url", additionalLinks[0]);
  fillProfileField(
    "extra_emails",
    Array.isArray(extracted.extra_emails) ? extracted.extra_emails.join(", ") : extracted.extra_emails
  );
  fillProfileField(
    "extra_phones",
    Array.isArray(extracted.extra_phones) ? extracted.extra_phones.join(", ") : extracted.extra_phones
  );
  fillProfileField(
    "extra_websites",
    [
      ...(Array.isArray(extracted.portfolio_urls) ? extracted.portfolio_urls : []),
      ...additionalLinks.slice(1),
    ].join(", ") || extracted.portfolio_urls
  );
  fillProfileField("location", extracted.location);
  // Always prefer AI-extracted title since local text extraction can confuse name with title
  if (preferAiTitle) {
    fillAlways("title", extracted.title);
  } else {
    fillIfEmpty("title", extracted.title);
  }
  fillProfileField("skills", Array.isArray(extracted.skills) ? extracted.skills.join(", ") : extracted.skills);
  fillProfileField(
    "company_client",
    extracted.company_client || extracted.current_company || extracted.currentCompany
  );

  return next;
}

function splitName(full) {
  const s = String(full || "").trim().replace(/\s+/g, " ");
  if (!s) return { first: "", last: "" };
  const parts = s.split(" ");
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

function splitLocationValue(location) {
  const parts = normalizeLocationValueForSave(location)
    .split(",")
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  if (!parts.length) return { city: "", state: "" };
  if (parts.length === 1) return { city: parts[0], state: "" };

  return {
    city: parts[0],
    state: parts.slice(1).join(", "),
  };
}

function joinName(first, last) {
  return [first, last].map((v) => String(v || "").trim()).filter(Boolean).join(" ");
}

function parseJsonValue(value, fallback = null) {
  if (value == null || value === "") return fallback;
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function parseJsonArrayValue(value) {
  if (Array.isArray(value)) return value;
  const parsed = parseJsonValue(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

function normalizeExistingCandidateOption(source = {}) {
  const preferences =
    source?.preferences_json && typeof source.preferences_json === "object"
      ? source.preferences_json
      : parseJsonValue(source?.preferences_json, {}) || {};
  const fullName =
    String(
      source?.full_name ||
        source?.candidate_name ||
        source?.name ||
        joinName(
          source?.first_name || preferences.first_name || "",
          source?.last_name || preferences.last_name || ""
        )
    )
      .trim()
      .replace(/\s+/g, " ") || "";
  const companyClient = normalizeCompanyName(
    source?.company_client ||
      source?.company ||
      source?.candidate_company ||
      source?.current_company ||
      source?.currentCompany ||
      preferences.company_client ||
      preferences.current_company ||
      preferences.currentCompany ||
      ""
  );
  const teamName = normalizeTeamMemberName(
    source?.team_name || preferences.assigned_team_member || preferences.team_name || ""
  );
  const candidateId = Number(source?.id ?? source?.candidate_id ?? 0);

  return {
    ...source,
    id: Number.isFinite(candidateId) && candidateId > 0 ? candidateId : source?.id ?? null,
    candidate_id:
      Number.isFinite(candidateId) && candidateId > 0
        ? candidateId
        : Number(source?.candidate_id || 0) || null,
    full_name: fullName,
    email: String(source?.email || "").trim(),
    phone: String(source?.phone || source?.phone_number || "").trim(),
    title: String(source?.title || source?.candidate_title || "").trim(),
    location: normalizeLocationValueForSave(source?.location || ""),
    owner: String(source?.owner || "").trim(),
    status: String(source?.status || source?.candidate_status || "New").trim(),
    stage: String(source?.stage || source?.candidate_stage || "").trim(),
    source: String(source?.source || preferences.source || "").trim(),
    work_authorization: String(
      source?.work_authorization ||
        preferences.work_authorization ||
        preferences.work_auhtorization ||
        ""
    ).trim(),
    resume_url: String(source?.resume_url || "").trim(),
    website: String(source?.website || preferences.website || "").trim(),
    website_url: String(
      source?.website_url || preferences.website_url || source?.website || preferences.website || ""
    ).trim(),
    linkedin_url: String(
      source?.linkedin_url || preferences.linkedin_url || preferences.linkedin || ""
    ).trim(),
    skills_array: cleanSkillsTokens(
      Array.isArray(source?.skills)
        ? source.skills
        : Array.isArray(preferences.skills)
        ? preferences.skills
        : splitCsvToArray(source?.skills || preferences.skills || "")
    ),
    preferences_json: preferences,
    experience_items: normalizeAiArray(parseJsonArrayValue(source?.experience_items)),
    education_items: normalizeAiArray(parseJsonArrayValue(source?.education_items)),
    company_client: companyClient,
    company_client_id: source?.company_client_id ?? preferences.company_client_id ?? null,
    company_client_logo: String(
      source?.company_client_logo || preferences.company_client_logo || ""
    ).trim(),
    team_name: teamName,
    other_url: String(preferences.other_url || "").trim(),
    extra_emails: splitCsvToArray(preferences.extra_emails || preferences.extraEmails || []),
    extra_phones: splitCsvToArray(preferences.extra_phones || preferences.extraPhones || []),
    extra_websites: splitCsvToArray(
      preferences.extra_websites || preferences.extraWebsites || []
    ),
    why_looking: String(
      source?.pref_why_looking || preferences.whyLooking || preferences.why_looking || ""
    ).trim(),
    pref_job_type: String(source?.pref_job_type || preferences.jobType || "").trim(),
    pref_job_types: splitCsvToArray(
      source?.pref_job_types || preferences.jobTypes || preferences.job_types || []
    ),
    pref_work_mode: String(source?.pref_work_mode || preferences.workMode || "").trim(),
    pref_work_modes: splitCsvToArray(
      source?.pref_work_modes || preferences.workModes || preferences.work_modes || []
    ),
    pref_salary_range: String(
      source?.pref_salary_range || preferences.salaryRange || ""
    ).trim(),
    pref_compensation: String(
      source?.pref_compensation || preferences.compensation || ""
    ).trim(),
    pref_availability: String(
      source?.pref_availability || preferences.availability || ""
    ).trim(),
    pref_timeline: String(source?.pref_timeline || preferences.timeline || "").trim(),
    pref_interview_availability: String(
      preferences.interviewAvailability || preferences.interview_availability || ""
    ).trim(),
    preferred_locations: normalizeLocationValuesForSave(
      source?.pref_locations || preferences.locations || preferences.pref_locations || []
    ),
    notes: String(preferences.notes || "").trim(),
    address: String(preferences.address || "").trim(),
    avatar_url: String(source?.avatar_url || "").trim(),
  };
}

function buildDialogStateFromExistingCandidate(source = {}, ownerName = "") {
  const candidate = normalizeExistingCandidateOption(source);
  const currentCompany =
    candidate.company_client ||
    normalizeCompanyName(
      candidate.preferences_json?.current_company || candidate.preferences_json?.currentCompany || ""
    );
  const websiteValue =
    deriveWebsiteValue(candidate.website, candidate.website_url) || candidate.website || "";
  const nameParts = splitName(candidate.full_name);

  return {
    candidate,
    form: {
      ...DEFAULT_FORM,
      full_name: candidate.full_name,
      email: candidate.email,
      phone: candidate.phone,
      title: candidate.title,
      location: candidate.location,
      address: candidate.address,
      owner: candidate.owner || ownerName,
      status: candidate.status || DEFAULT_FORM.status,
      stage: candidate.stage || DEFAULT_FORM.stage,
      resume_url: candidate.resume_url,
      linkedin_url: candidate.linkedin_url,
      skills: candidate.skills_array.join(", "),
      work_authorization: candidate.work_authorization,
      work_auhtorization: candidate.work_authorization,
      notes: candidate.notes,
      source: candidate.source || DEFAULT_FORM.source,
      website: websiteValue,
      website_url: candidate.website_url,
      other_url: candidate.other_url,
      company_client: candidate.company_client,
      company_client_id: candidate.company_client_id,
      company_client_logo: candidate.company_client_logo,
      assigned_team_member: candidate.team_name,
      team_name: candidate.team_name,
      extra_emails: candidate.extra_emails.join(", "),
      extra_phones: candidate.extra_phones.join(", "),
      extra_websites: candidate.extra_websites.join(", "),
      why_looking: candidate.why_looking,
      job_types: candidate.pref_job_types.join(", "),
      work_modes: candidate.pref_work_modes.join(", "),
      timeline: candidate.pref_timeline,
      compensation: candidate.pref_compensation,
      interview_availability: candidate.pref_interview_availability,
      preferred_job_type: candidate.pref_job_type,
      preferred_work_mode: candidate.pref_work_mode,
      salary_range: candidate.pref_salary_range,
      availability: candidate.pref_availability,
      preferred_locations: candidate.preferred_locations.join(", "),
    },
    firstName: nameParts.first,
    lastName: nameParts.last,
    companyInputValue: candidate.company_client,
    candidateLookupInputValue: candidate.full_name,
    candidateLinkInput:
      candidate.linkedin_url || candidate.website_url || candidate.other_url || "",
    resumeAiData: mergeResumeAiData({
      company_client: currentCompany,
      current_company: currentCompany,
      linkedin_url: candidate.linkedin_url || "",
      experience_items: candidate.experience_items,
      education_items: candidate.education_items,
    }),
    avatarPreview: candidate.avatar_url || "",
  };
}

function isCurrentExperienceRange(value) {
  const normalized = String(value || "").trim();
  return !normalized || /present|current|now|today/i.test(normalized);
}

function toExperienceDateScore(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return Number.NEGATIVE_INFINITY;
  if (isCurrentExperienceRange(normalized)) return Date.now();

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

  const yearMatch = normalized.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    const fallback = new Date(Number(yearMatch[0]), 0, 1);
    if (!Number.isNaN(fallback.getTime())) return fallback.getTime();
  }

  return Number.NEGATIVE_INFINITY;
}

function getOrderedExperienceCompanies(experienceItems = []) {
  const normalizedItems = normalizeAiArray(experienceItems)
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      ...item,
      company: normalizeCompanyName(item.company || item.employer || ""),
      to: String(item.to || item.end || item.endDate || "").trim(),
      from: String(item.from || item.start || item.startDate || "").trim(),
    }))
    .filter((item) => item.company);

  normalizedItems.sort((a, b) => {
    const currentDelta =
      Number(isCurrentExperienceRange(b.to)) - Number(isCurrentExperienceRange(a.to));
    if (currentDelta) return currentDelta;

    const endDelta = toExperienceDateScore(b.to) - toExperienceDateScore(a.to);
    if (endDelta) return endDelta;

    return toExperienceDateScore(b.from) - toExperienceDateScore(a.from);
  });

  const seen = new Set();
  return normalizedItems
    .map((item) => item.company)
    .filter((company) => {
      const key = company.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function inferCurrentCompanyFromExperienceItems(experienceItems = []) {
  return getOrderedExperienceCompanies(experienceItems)[0] || "";
}

function inferPastCompaniesFromExperienceItems(experienceItems = []) {
  const companies = getOrderedExperienceCompanies(experienceItems);
  if (!companies.length) return [];

  const currentCompany = String(companies[0] || "").toLowerCase();
  return companies.filter((company) => company.toLowerCase() !== currentCompany);
}

function readOverviewDraft() {
  try {
    const raw = sessionStorage.getItem(OVERVIEW_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function clearOverviewDraft() {
  try {
    sessionStorage.removeItem(OVERVIEW_DRAFT_KEY);
  } catch {
    // ignore storage failures
  }
}

function normalizeCompanyName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function extractCompaniesList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.companies)) return data.companies;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.payload?.companies)) return data.payload.companies;
  if (Array.isArray(data?.payload?.rows)) return data.payload.rows;
  if (Array.isArray(data?.payload?.items)) return data.payload.items;
  if (Array.isArray(data?.payload?.data)) return data.payload.data;
  if (Array.isArray(data?.data?.companies)) return data.data.companies;
  if (Array.isArray(data?.data?.rows)) return data.data.rows;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  return [];
}

function extractJobsList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.jobs)) return data.jobs;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data?.jobs)) return data.data.jobs;
  if (Array.isArray(data?.payload?.jobs)) return data.payload.jobs;
  return [];
}

function normalizeCompanyLogo(value, apiBase = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const isLocalhostUrl = (url) =>
    /^http:\/\/localhost(?::\d+)?\//i.test(url) ||
    /^http:\/\/127\.0\.0\.1(?::\d+)?\//i.test(url) ||
    /^https?:\/\/localhost(?::\d+)?\//i.test(url) ||
    /^https?:\/\/127\.0\.0\.1(?::\d+)?\//i.test(url);

  if (/^https?:\/\//i.test(raw)) {
    return isLocalhostUrl(raw) ? raw : raw.replace(/^http:\/\//i, "https://");
  }

  const cleanBase = String(apiBase || "").replace(/\/$/, "");
  const cleanValue = raw.startsWith("/") ? raw : `/${raw}`;
  const fullUrl = `${cleanBase}${cleanValue}`;

  return isLocalhostUrl(fullUrl)
    ? fullUrl
    : fullUrl.replace(/^http:\/\//i, "https://");
}

function resolveCompanyValue(value, companies = []) {
  if (typeof value === "string") {
    const typed = normalizeCompanyName(value);
    const exact = companies.find(
      (c) =>
        normalizeCompanyName(c.name).toLowerCase() === typed.toLowerCase() ||
        String(c.id).toLowerCase() === typed.toLowerCase()
    );

    if (exact) {
      return {
        company_client: exact.name,
        company_client_id: exact.id ?? null,
        company_client_logo: exact.logo || "",
        company_website: String(exact.website || "").trim(),
      };
    }

    return {
      company_client: typed,
      company_client_id: null,
      company_client_logo: "",
      company_website: "",
    };
  }

  if (value?.inputValue) {
    const typed = normalizeCompanyName(value.inputValue);
    const exact = companies.find(
      (c) =>
        normalizeCompanyName(c.name).toLowerCase() === typed.toLowerCase() ||
        String(c.id).toLowerCase() === typed.toLowerCase()
    );

    if (exact) {
      return {
        company_client: exact.name,
        company_client_id: exact.id ?? null,
        company_client_logo: exact.logo || "",
        company_website: String(exact.website || "").trim(),
      };
    }

    return {
      company_client: typed,
      company_client_id: null,
      company_client_logo: "",
      company_website: "",
    };
  }

  if (value?.name) {
    return {
      company_client: normalizeCompanyName(value.name),
      company_client_id: value.id ?? null,
      company_client_logo: value.logo || "",
      company_website: String(value.website || "").trim(),
    };
  }

  return {
    company_client: "",
    company_client_id: null,
    company_client_logo: "",
    company_website: "",
  };
}

async function parseJsonSafe(res) {
  const rawText = await res.text();
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    return {};
  }
}

async function requestJson({
  url,
  method = "GET",
  token,
  body,
  isFormData = false,
}) {
  const headers = {
    Accept: "application/json",
  };

  if (token) headers.Authorization = `Bearer ${token}`;
  if (!isFormData) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers,
    body: body == null ? undefined : isFormData ? body : JSON.stringify(body),
  });

  const data = await parseJsonSafe(res);
  return { res, data };
}

function splitCsvToArray(input) {
  if (Array.isArray(input)) {
    return input
      .map((v) => String(v || "").trim())
      .filter(Boolean);
  }

  const raw = String(input || "").trim();
  if (!raw || raw === "[]" || /^null$/i.test(raw)) {
    return [];
  }

  const parsed = parseJsonValue(raw, null);
  if (Array.isArray(parsed)) {
    return parsed
      .map((v) => String(v || "").trim())
      .filter(Boolean);
  }

  return raw
    .split(/[,\n|]/g)
    .map((v) => String(v || "").trim())
    .filter(Boolean);
}

function deriveWebsiteValue(rawWebsite, rawWebsiteUrl) {
  const direct = String(rawWebsite || "").trim();
  const url = safeUrl(rawWebsiteUrl || rawWebsite || "");
  if (direct) return direct;
  if (!url) return "";
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./i, "");
  } catch {
    return String(rawWebsiteUrl || "").trim();
  }
}

function buildSocialLinks({ linkedin_url, website_url, other_url }) {
  return [linkedin_url, website_url, other_url]
    .map((v) => safeUrl(v))
    .filter(Boolean);
}

function normalizeAiArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === "string") return item.trim();
    if (item && typeof item === "object") {
      const next = {};
      Object.entries(item).forEach(([k, v]) => {
        next[k] = typeof v === "string" ? v.trim() : v;
      });
      return next;
    }
    return item;
  });
}

function normalizeUniqueStrings(values) {
  const seen = new Set();
  return splitCsvToArray(values).filter((value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return false;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function mergeResumeAiData(...sources) {
  const next = {
    ...EMPTY_RESUME_AI,
    experience_items: [],
    education_items: [],
    recruitment_tools: [],
    job_boards: [],
  };

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    const experienceItems = normalizeAiArray(source.experience_items);
    if (experienceItems.length) next.experience_items = experienceItems;

    const educationItems = normalizeAiArray(source.education_items);
    if (educationItems.length) next.education_items = educationItems;

    for (const field of RESUME_AI_LIST_FIELDS) {
      const items = normalizeUniqueStrings(source[field]);
      if (items.length) next[field] = items;
    }

    for (const field of RESUME_AI_TEXT_FIELDS) {
      const value = String(source[field] || "").trim();
      if (value) next[field] = value;
    }
  }

  const inferredSocialLinks = buildCompanySocialPayloadFromLinks([
    next.linkedin_url,
    next.facebook_url,
    next.twitter_url,
    next.instagram_url,
    next.youtube_url,
    next.tiktok_url,
    next.company_link,
    next.company_website,
    next.career_site,
  ]);

  next.linkedin_url = next.linkedin_url || inferredSocialLinks.linkedin_url;
  next.facebook_url = next.facebook_url || inferredSocialLinks.facebook_url;
  next.twitter_url = next.twitter_url || inferredSocialLinks.twitter_url;
  next.instagram_url = next.instagram_url || inferredSocialLinks.instagram_url;
  next.youtube_url = next.youtube_url || inferredSocialLinks.youtube_url;
  next.tiktok_url = next.tiktok_url || inferredSocialLinks.tiktok_url;

  if (!next.current_company && next.company_client) {
    next.current_company = next.company_client;
  }
  if (!next.company_client && next.current_company) {
    next.company_client = next.current_company;
  }

  const primaryCompanyWebsite = getPrimaryCompanyWebsite({
    company_website: next.company_website,
    company_link: next.company_link,
    career_site: next.career_site,
  });

  if (!next.company_website || isCompanySocialUrl(next.company_website)) {
    next.company_website = primaryCompanyWebsite || "";
  }
  if (next.career_site && isCompanySocialUrl(next.career_site)) {
    next.career_site = "";
  }
  if (!next.company_link && next.company_website) {
    next.company_link = next.company_website;
  }
  if (!next.industry_sector && next.industry) {
    next.industry_sector = next.industry;
  }
  if (!next.industry && next.industry_sector) {
    next.industry = next.industry_sector;
  }
  if (!next.company_size && next.size_range) {
    next.company_size = next.size_range;
  }
  if (!next.size_range && next.company_size) {
    next.size_range = next.company_size;
  }

  return next;
}

function normalizeCompanyAiProfile(source = {}, options = {}) {
  const includeGenericLocation = options?.includeGenericLocation === true;
  const includeSocialHints = options?.includeSocialHints !== false;
  const input = source && typeof source === "object" ? source : {};
  const normalizedName = normalizeCompanyName(
    input.company_client ||
      input.current_company ||
      input.currentCompany ||
      input.company_name ||
      input.companyName ||
      ""
  );
  const normalizedCompanyLink =
    safeUrl(input.company_link || input.companyLink || input.company_url || input.companyUrl || "") ||
    "";
  const normalizedCareerSite =
    safeUrl(
      input.career_site ||
        input.career_website ||
        input.company_career_url ||
        input.company_career_website ||
        ""
    ) || "";
  const normalizedSocialLinks = buildCompanySocialPayloadFromLinks([
    ...(includeSocialHints
      ? [
          input.linkedin_url,
          input.linkedinUrl,
          input.facebook_url,
          input.facebookUrl,
          input.twitter_url,
          input.twitterUrl,
          input.instagram_url,
          input.instagramUrl,
          input.youtube_url,
          input.youtubeUrl,
          input.tiktok_url,
          input.tiktokUrl,
        ]
      : []),
    normalizedCompanyLink,
    input.company_website,
    input.companyWebsite,
    input.company_url,
    input.companyUrl,
    normalizedCareerSite,
  ]);
  const normalizedCompanyWebsite = getPrimaryCompanyWebsite({
    company_website:
      safeUrl(
        input.company_website ||
          input.companyWebsite ||
          input.company_url ||
          input.companyUrl ||
          ""
      ) || "",
    company_link: normalizedCompanyLink,
    career_site: normalizedCareerSite,
  });

  return mergeResumeAiData({
    company_client: normalizedName,
    current_company: normalizedName,
    company_website: normalizedCompanyWebsite || "",
    company_link: normalizedCompanyLink || normalizedCompanyWebsite || "",
    career_site: normalizedCareerSite && !isCompanySocialUrl(normalizedCareerSite) ? normalizedCareerSite : "",
    company_location: includeGenericLocation
      ? normalizeLocationValueForSave(input.company_location || input.location || "")
      : normalizeLocationValueForSave(input.company_location || ""),
    company_description: String(
      input.company_description || input.full_company_description || ""
    ).trim(),
    full_company_description: String(
      input.full_company_description || input.company_description || ""
    ).trim(),
    elevator_pitch: String(input.elevator_pitch || "").trim(),
    mission_and_vision: String(input.mission_and_vision || "").trim(),
    key_products_services: String(input.key_products_services || "").trim(),
    unique_selling_points: String(input.unique_selling_points || "").trim(),
    employee_perks: String(input.employee_perks || "").trim(),
    achievements_awards: String(input.achievements_awards || "").trim(),
    industry: String(input.industry || input.industry_sector || "").trim(),
    industry_sector: String(input.industry_sector || input.industry || "").trim(),
    size_range: String(input.size_range || input.company_size || "").trim(),
    company_size: String(input.company_size || input.size_range || "").trim(),
    founded_year: String(input.founded_year || input.founded || "").trim(),
    ownership_type: String(input.ownership_type || "").trim(),
    parent_company: String(
      input.parent_company || input.parent_company_subsidiaries || ""
    ).trim(),
    work_policies: String(input.work_policies || "").trim(),
    recruitment_tools: input.recruitment_tools || input.recruitmentTools || [],
    job_boards: input.job_boards || input.jobBoards || [],
    job_board_rating: String(
      input.job_board_rating || input.glassdoor_rating || ""
    ).trim(),
    company_email: String(input.company_email || "").trim(),
    company_phone: String(input.company_phone || "").trim(),
    linkedin_url: normalizedSocialLinks.linkedin_url,
    facebook_url: normalizedSocialLinks.facebook_url,
    twitter_url: normalizedSocialLinks.twitter_url,
    instagram_url: normalizedSocialLinks.instagram_url,
    youtube_url: normalizedSocialLinks.youtube_url,
    tiktok_url: normalizedSocialLinks.tiktok_url,
  });
}

function hasCompanyProfileData(source = {}) {
  if (!source || typeof source !== "object") return false;

  if (
    RESUME_AI_LIST_FIELDS.some((field) =>
      normalizeUniqueStrings(source[field]).length > 0
    )
  ) {
    return true;
  }

  return COMPANY_PROFILE_CHECK_FIELDS.some((field) => String(source[field] || "").trim());
}

async function callCreatedCallback(onCreated, createdCandidate, context = {}) {
  if (typeof onCreated !== "function") return;

  const result = onCreated(createdCandidate, {
    refresh: true,
    source: "add-candidate-dialog",
    ...context,
  });

  if (result && typeof result.then === "function") {
    await result;
  }
}

export default function AddCandidateDialog({
  open,
  onClose,
  onCreated,
  user,
  token,
  apiBase = API_BASE,
  initialScreen = null,
  openProfileOnComplete = true,
  requireOnCreatedSuccess = false,
  jobStageOptions = false,
  existingCandidateOnly = false,
  showCandidateLookupField = true,
  showStageFieldForExistingCandidate = false,
  existingCandidateSubmitLabel = "Add to Job",
}) {
  const navigate = useNavigate();
  const [screen, setScreen] = useState("start");

  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [creatingLinkCompany, setCreatingLinkCompany] = useState(false);
  const [startedFromLinkFlow, setStartedFromLinkFlow] = useState(false);
  const [candidateImportMode, setCandidateImportMode] = useState("");
  const [uploadAutoCreateStage, setUploadAutoCreateStage] = useState("idle");
  const [candidateLinkInput, setCandidateLinkInput] = useState("");

  const [resumeFile, setResumeFile] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  const [parsingResume, setParsingResume] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyInputValue, setCompanyInputValue] = useState("");
  const [companiesDebug, setCompaniesDebug] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersDebug, setTeamMembersDebug] = useState("");
  const [companyContacts, setCompanyContacts] = useState([]);
  const [companyContactsLoading, setCompanyContactsLoading] = useState(false);
  const [companyContactsDebug, setCompanyContactsDebug] = useState("");
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [candidateLookupOptions, setCandidateLookupOptions] = useState([]);
  const [candidateLookupLoading, setCandidateLookupLoading] = useState(false);
  const [candidateLookupDebug, setCandidateLookupDebug] = useState("");
  const [candidateLookupInputValue, setCandidateLookupInputValue] = useState("");
  const candidateSearchCacheRef = useRef(new Map());
  const [selectedExistingCandidate, setSelectedExistingCandidate] = useState(null);

  const [resumeAiData, setResumeAiData] = useState(EMPTY_RESUME_AI);
  const [primaryCompanyPickerOpen, setPrimaryCompanyPickerOpen] = useState(false);
  const [extractedResumeCompanies, setExtractedResumeCompanies] = useState([]);
  const [selectedPrimaryCompanies, setSelectedPrimaryCompanies] = useState([]);
  const [companyJobPickerOpen, setCompanyJobPickerOpen] = useState(false);
  const [companyJobPickerLoading] = useState(false);
  const [companyJobPickerSaving, setCompanyJobPickerSaving] = useState(false);
  const [companyJobScanProgress] = useState({ completed: 0, total: 0, companyName: "" });
  const [companyJobRows] = useState([]);
  const [selectedCompanyJobIds, setSelectedCompanyJobIds] = useState([]);
  const [approvedCompanyRecords] = useState([]);
  const [createdCandidateForCompanyJobs] = useState(null);
  const [candidateImportHandoff, setCandidateImportHandoff] = useState(false);
  const parsedResumeTextRef = useRef("");
  const resumeAiParseCompleteRef = useRef(false);
  const pendingUploadAutoSubmitRef = useRef(false);
  const pendingResumeBackgroundParseRef = useRef(false);
  const normalizedJobStageOptions = useMemo(() => {
    const source =
      jobStageOptions === true
        ? JOB_MATCH_STAGE_OPTIONS
        : Array.isArray(jobStageOptions) && jobStageOptions.length
          ? jobStageOptions
          : [];

    return source
      .map((option) => {
        if (typeof option === "string") {
          const label = option.trim();
          return label ? { value: label, label } : null;
        }

        const value = String(option?.value || option?.label || "").trim();
        const label = String(option?.label || option?.value || "").trim();
        return value && label ? { value, label } : null;
      })
      .filter(Boolean);
  }, [jobStageOptions]);
  const showJobStageField = normalizedJobStageOptions.length > 0;
  const existingCompanyIdsByName = useMemo(
    () =>
      new Map(
        companies
          .map((company) => [
            normalizeCompanyName(company?.name || "").toLowerCase(),
            company?.id ?? company?.company_id ?? null,
          ])
          .filter(([name, id]) => name && id != null)
      ),
    [companies]
  );
  const companyJobColumns = useMemo(
    () => [
      { id: "company_name", label: "Company Name" },
      { id: "job_title", label: "Opening" },
      { id: "location", label: "Location" },
      { id: "employment_type", label: "Type" },
      { id: "job_url", label: "Source", sortable: false },
    ],
    []
  );
  const companySelectionColumns = useMemo(
    () => [
      { id: "company_name", label: "Company extracted from CV" },
      { id: "company_status", label: "Status", sortable: false },
    ],
    []
  );
  const requiresJobAndStageSelection =
    candidateImportMode === "link" || candidateImportMode === "resume";
  const shouldRestrictToExistingCandidate = existingCandidateOnly === true;
  const shouldShowOverviewStageField =
    showJobStageField &&
    (!shouldRestrictToExistingCandidate || showStageFieldForExistingCandidate);

  const getToken = useCallback(
    () => {
      const storedToken =
        token ||
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("jwt") ||
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("jwt") ||
        sessionStorage.getItem("accessToken") ||
        "";
      return String(storedToken || "").trim();
    },
    [token]
  );

  const ownerName = useMemo(() => {
    const fromUser =
      user?.name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
      user?.username ||
      user?.email ||
      "";
    if (fromUser) return fromUser;

    const t = getToken();
    const decoded = safeDecodeJwt(t);
    const fromJwt =
      decoded?.name ||
      [decoded?.first_name, decoded?.last_name].filter(Boolean).join(" ").trim() ||
      decoded?.username ||
      decoded?.email ||
      "";
    return fromJwt || "Unknown";
  }, [getToken, user]);

  const clearSelectedExistingCandidate = useCallback((nextLookupValue = "") => {
    setSelectedExistingCandidate(null);
    setCandidateLookupInputValue(String(nextLookupValue || "").trim());
  }, []);

  const companyGetUrls = useMemo(
    () => [
      `${apiBase}/api/companies?page=1&limit=5000&compact=1`,
      `${apiBase}/api/companies?limit=5000&compact=1`,
      `${apiBase}/api/companies`,
      `${apiBase}/companies?page=1&limit=5000&compact=1`,
      `${apiBase}/companies?limit=5000&compact=1`,
      `${apiBase}/companies`,
    ],
    [apiBase]
  );

  const companyPostUrls = useMemo(
    () => [`${apiBase}/api/companies`, `${apiBase}/companies`],
    [apiBase]
  );

  const candidateLinkAiUrls = useMemo(
    () => [`${apiBase}/api/candidates/parse-link`, `${apiBase}/candidates/parse-link`],
    [apiBase]
  );

  const fetchCompanies = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) {
      setCompanies([]);
      setCompaniesDebug("No auth token");
      return [];
    }

    setCompaniesLoading(true);
    setCompaniesDebug("");

    try {
      let finalCompanies = [];
      let lastDebug = "";

      for (const url of companyGetUrls) {
        try {
          const { res, data } = await requestJson({
            url,
            method: "GET",
            token: authToken,
          });

          const list = extractCompaniesList(data);
          const normalized = list
            .map((c) => ({
              id: c?.id ?? c?.company_id ?? c?.ID ?? null,
              name: normalizeCompanyName(c?.name ?? c?.company_name ?? c?.title ?? ""),
              logo: normalizeCompanyLogo(c?.logo ?? c?.company_logo ?? "", apiBase),
              website: String(c?.website ?? c?.company_website ?? c?.companyWebsite ?? "").trim(),
            }))
            .filter((c) => c.id != null && c.name)
            .sort((a, b) => a.name.localeCompare(b.name));

          lastDebug = `URL: ${url} | status: ${res.status} | extracted: ${normalized.length}`;

          if (res.ok && normalized.length) {
            finalCompanies = normalized;
            break;
          }
        } catch (err) {
          lastDebug = `Failed: ${url} | ${err?.message || "unknown error"}`;
        }
      }

      setCompanies(finalCompanies);
      setCompaniesDebug(lastDebug || "No companies route responded");
      return finalCompanies;
    } finally {
      setCompaniesLoading(false);
    }
  }, [apiBase, companyGetUrls, getToken]);

  const fetchJobs = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) {
      setJobs([]);
      return;
    }

    setJobsLoading(true);
    try {
      const { res, data } = await requestJson({
        url: `${apiBase}/api/jobs?limit=5000`,
        method: "GET",
        token: authToken,
      });
      if (!res.ok) throw new Error(data?.error || "Failed to load jobs");

      setJobs(
        extractJobsList(data)
          .map((job) => {
            const id = job?.id ?? job?.job_id ?? job?.ID;
            const title = String(job?.title || job?.job_title || job?.name || "").trim();
            const company = String(
              job?.company_name || job?.company || job?.company_client || ""
            ).trim();
            return id != null && title
              ? { value: String(id), label: company ? `${title} — ${company}` : title }
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [apiBase, getToken]);

  const fetchTeamMembersList = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) {
      setTeamMembers([]);
      setTeamMembersDebug("No auth token");
      return;
    }

    setTeamMembersLoading(true);
    setTeamMembersDebug("");

    try {
      const rows = await fetchTeamMembers({ apiBase, token: authToken });
      setTeamMembers(rows);
      setTeamMembersDebug(rows.length ? "" : "No team members found");
    } catch (error) {
      setTeamMembers([]);
      setTeamMembersDebug(error?.message || "Failed to load team members");
    } finally {
      setTeamMembersLoading(false);
    }
  }, [apiBase, getToken]);

  const buildCompanyCreatePayload = useCallback(
    (companyName, options = {}) => {
      const companyData = mergeResumeAiData(normalizeCompanyAiProfile(options), options);
      const normalizedName = normalizeCompanyName(
        companyName || companyData.company_client || companyData.current_company || ""
      );
      const companySocialLinks = buildCompanySocialPayloadFromLinks([
        companyData.linkedin_url,
        companyData.facebook_url,
        companyData.twitter_url,
        companyData.instagram_url,
        companyData.youtube_url,
        companyData.tiktok_url,
        companyData.company_link,
        companyData.company_website,
        companyData.career_site,
      ]);
      const companyWebsite =
        getPrimaryCompanyWebsite({
          company_website: companyData.company_website,
          company_link: companyData.company_link,
          career_site: companyData.career_site,
        }) || "";
      const companyCareerSite =
        companyData.career_site && !isCompanySocialUrl(companyData.career_site)
          ? companyData.career_site
          : "";
      const companyLink =
        safeUrl(companyData.company_link || "") ||
        companyWebsite ||
        companyCareerSite ||
        companySocialLinks.linkedin_url ||
        companySocialLinks.facebook_url ||
        companySocialLinks.twitter_url ||
        companySocialLinks.instagram_url ||
        companySocialLinks.youtube_url ||
        companySocialLinks.tiktok_url ||
        "";
      const companyDescription =
        companyData.full_company_description || companyData.company_description || "";
      const companyEmail = companyData.company_email || "";
      const companyPhone = companyData.company_phone || "";
      const companySource = String(options.source || "Candidate").trim() || "Candidate";
      const companyLocation = String(
        companyData.company_location || options.company_location || options.location || ""
      ).trim();
      const companyIndustry = companyData.industry || companyData.industry_sector || "";
      const companyIndustrySector = companyData.industry_sector || companyData.industry || "";
      const companySize = companyData.size_range || companyData.company_size || "";
      const companySizeValue = companyData.company_size || companyData.size_range || "";
      const companyStatus = String(options.status || "Active").trim() || "Active";
      const companyNotes =
        String(options.notes || "").trim() ||
        `Created from candidate import${companySource ? ` (${companySource})` : ""}`;
      const interactiveImport = Boolean(
        options?.interactive_import || options?.interactiveImport
      );

      return {
        name: normalizedName,
        logo: "",
        website: companyWebsite,
        company_website: companyWebsite,
        company_link: companyLink,
        company_url: companyLink,
        career_site: companyCareerSite,
        career_website: companyCareerSite,
        company_career_url: companyCareerSite,
        company_career_website: companyCareerSite,
        company_location: companyLocation,
        location: companyLocation,
        company_description: companyData.company_description || companyDescription,
        full_company_description: companyDescription,
        elevator_pitch: companyData.elevator_pitch || "",
        mission_and_vision: companyData.mission_and_vision || "",
        key_products_services: companyData.key_products_services || "",
        unique_selling_points: companyData.unique_selling_points || "",
        employee_perks: companyData.employee_perks || "",
        achievements_awards: companyData.achievements_awards || "",
        industry: companyIndustry,
        industry_sector: companyIndustrySector,
        size_range: companySize,
        company_size: companySizeValue,
        founded_year: companyData.founded_year || "",
        ownership_type: companyData.ownership_type || "",
        parent_company: companyData.parent_company || "",
        parent_company_subsidiaries: companyData.parent_company || "",
        work_policies: companyData.work_policies || "",
        recruitment_tools: companyData.recruitment_tools || [],
        job_boards: companyData.job_boards || [],
        job_board_rating: companyData.job_board_rating || "",
        email: companyEmail,
        company_email: companyEmail,
        phone: companyPhone,
        company_phone: companyPhone,
        linkedin_url: companySocialLinks.linkedin_url,
        facebook_url: companySocialLinks.facebook_url,
        twitter_url: companySocialLinks.twitter_url,
        instagram_url: companySocialLinks.instagram_url,
        youtube_url: companySocialLinks.youtube_url,
        tiktok_url: companySocialLinks.tiktok_url,
        status: companyStatus,
        source: companySource,
        notes: companyNotes,
        owner_name: ownerName,
        owner: ownerName,
        team_member: ownerName,
        candidate_import: true,
        contact_source_policy: "none",
        ...(interactiveImport ? { interactive_import: true } : {}),
      };
    },
    [ownerName]
  );

  const createCompanyIfMissing = useCallback(
    async (authToken, companyName, options = {}) => {
      const normalizedName = normalizeCompanyName(companyName);
      if (!normalizedName) {
        return { id: null, name: "", logo: "" };
      }

      const existing =
        companies.find((c) => c.name.toLowerCase() === normalizedName.toLowerCase()) ||
        companies.find((c) => String(c.id).toLowerCase() === normalizedName.toLowerCase()) ||
        null;

      const shouldAttemptBackfill =
        options.skipProfileBackfill !== true &&
        hasCompanyProfileData(mergeResumeAiData(normalizeCompanyAiProfile(options), options));

      if (existing && !shouldAttemptBackfill) return existing;

      const basePayload = buildCompanyCreatePayload(normalizedName, options);

      const payloads = [
        basePayload,
        { ...basePayload, status: undefined },
        {
          name: normalizedName,
          owner_name: ownerName,
          owner: ownerName,
          team_member: ownerName,
        },
        {
          ...basePayload,
          company_name: normalizedName,
        },
      ];

      let lastError = null;

      for (const url of companyPostUrls) {
        for (const body of payloads) {
          try {
            const { res, data } = await requestJson({
              url,
              method: "POST",
              token: authToken,
              body,
            });

            if (!res.ok) {
              lastError = new Error(
                data?.error || data?.message || `Failed to create company (${res.status})`
              );
              continue;
            }

            const maybeCompany =
              data?.company ||
              data?.data?.company ||
              data?.data ||
              data?.payload?.company ||
              data;

            const created = {
              id: maybeCompany?.id ?? maybeCompany?.company_id ?? maybeCompany?.ID ?? null,
              name: normalizeCompanyName(
                maybeCompany?.name ?? maybeCompany?.company_name ?? normalizedName
              ),
              logo: normalizeCompanyLogo(
                maybeCompany?.logo ?? maybeCompany?.company_logo ?? "",
                apiBase
              ),
              website: String(
                maybeCompany?.website ??
                  maybeCompany?.company_website ??
                  basePayload.company_website ??
                  ""
              ).trim(),
            };

            if (created.id != null) {
              setCompanies((prev) => {
                const exists = prev.some((c) => String(c.id) === String(created.id));
                if (exists) return prev;
                return [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
              });
              return created;
            }
          } catch (err) {
            lastError = err;
          }
        }
      }

      const refreshedCompanies = await fetchCompanies();

      const foundAfterRefresh = (Array.isArray(refreshedCompanies) ? refreshedCompanies : companies).find(
        (c) => c.name.toLowerCase() === normalizedName.toLowerCase()
      );
      if (foundAfterRefresh) return foundAfterRefresh;

      throw lastError || new Error("Failed to create company");
    },
    [apiBase, buildCompanyCreatePayload, companies, companyPostUrls, fetchCompanies, ownerName]
  );

  const parseCandidateLinkWithOpenAI = useCallback(
    async ({ authToken, url }) => {
      let lastError = null;

      for (const endpoint of candidateLinkAiUrls) {
        try {
          const { res, data } = await requestJson({
            url: endpoint,
            method: "POST",
            token: authToken,
            body: { url },
          });

          if (!res.ok) {
            lastError = new Error(
              data?.error || data?.message || `Candidate link parse failed (${res.status})`
            );
            continue;
          }

          const parsed =
            data?.parsed ||
            data?.data?.parsed ||
            data?.result ||
            data?.data ||
            data;
          const companyProfile = normalizeCompanyAiProfile(parsed, {
            includeSocialHints: false,
          });

          return {
            full_name: String(parsed?.full_name || "").trim(),
            email: String(parsed?.email || "").trim(),
            phone: String(parsed?.phone || "").trim(),
            title: String(parsed?.title || "").trim(),
            location: normalizeLocationValueForSave(parsed?.location || ""),
            linkedin_url: String(parsed?.linkedin_url || "").trim(),
            website_url: String(parsed?.website_url || "").trim(),
            website: String(parsed?.website || "").trim(),
            portfolio_urls: Array.isArray(parsed?.portfolio_urls)
              ? parsed.portfolio_urls.map((value) => String(value || "").trim()).filter(Boolean)
              : [],
            social_links: Array.isArray(parsed?.social_links)
              ? parsed.social_links.map((value) => String(value || "").trim()).filter(Boolean)
              : [],
            extra_emails: Array.isArray(parsed?.extra_emails)
              ? parsed.extra_emails.map((value) => String(value || "").trim()).filter(Boolean)
              : [],
            extra_phones: Array.isArray(parsed?.extra_phones)
              ? parsed.extra_phones.map((value) => String(value || "").trim()).filter(Boolean)
              : [],
            link_kind: String(data?.link_kind || data?.linkKind || parsed?.link_kind || "").trim(),
            parse_warning: String(data?.warning || parsed?.parse_warning || "").trim(),
            ...companyProfile,
            skills: Array.isArray(parsed?.skills)
              ? cleanSkillsTokens(parsed.skills)
              : splitCsvToArray(parsed?.skills),
            experience_items: normalizeAiArray(parsed?.experience_items),
            education_items: normalizeAiArray(parsed?.education_items),
            past_companies: Array.isArray(parsed?.past_companies)
              ? parsed.past_companies.map((item) => normalizeCompanyName(item))
              : Array.isArray(parsed?.pastCompanies)
              ? parsed.pastCompanies.map((item) => normalizeCompanyName(item))
              : [],
          };
        } catch (err) {
          lastError = err;
        }
      }

      if (lastError) throw lastError;
      return null;
    },
    [candidateLinkAiUrls]
  );

  useEffect(() => {
    if (!open) return;

    const draft = shouldRestrictToExistingCandidate ? null : readOverviewDraft();
    const nextInitialScreen = normalizeCandidateDialogScreen(
      initialScreen,
      shouldRestrictToExistingCandidate
        ? "overview"
        : draft?.form
          ? "overview"
          : "start"
    );
    const next = {
      ...DEFAULT_FORM,
      owner: ownerName,
      ...(draft?.form || {}),
    };
    next.owner = ownerName;
    setForm(next);
    setErrors({});
    setSaving(false);
    setParsingResume(false);
    setCandidateImportHandoff(false);

    setResumeFile(null);
    setAvatarFile(null);
    setAvatarPreview("");
    setCompanyInputValue(next.company_client || "");
    setCompaniesDebug("");
    setTeamMembersDebug("");
    setCandidateLookupOptions([]);
    setCandidateLookupLoading(false);
    setCandidateLookupDebug("");
    setCandidateLookupInputValue("");
    setSelectedExistingCandidate(null);
    setResumeAiData(EMPTY_RESUME_AI);
    parsedResumeTextRef.current = "";
    resumeAiParseCompleteRef.current = false;
    pendingUploadAutoSubmitRef.current = false;
    setCandidateLinkInput(next.linkedin_url || next.website_url || next.other_url || "");
    setStartedFromLinkFlow(false);
    setCandidateImportMode("");
    setUploadAutoCreateStage("idle");

    setScreen(nextInitialScreen);

    const np = splitName(draft?.form?.full_name || next.full_name);
    setFirstName(draft?.firstName ?? np.first);
    setLastName(draft?.lastName ?? np.last);

    if (!shouldRestrictToExistingCandidate) {
      fetchCompanies();
      fetchJobs();
      fetchTeamMembersList();
    }
  }, [
    open,
    ownerName,
    fetchCompanies,
    fetchJobs,
    fetchTeamMembersList,
    initialScreen,
    shouldRestrictToExistingCandidate,
  ]);

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  useEffect(() => {
    const np = splitName(form.full_name);
    setFirstName(np.first);
    setLastName(np.last);
  }, [form.full_name]);

  useEffect(() => {
    if (!open || screen !== "overview") return;

    const authToken = getToken();
    if (!authToken) {
      setCandidateLookupOptions([]);
      setCandidateLookupLoading(false);
      setCandidateLookupDebug("No auth token");
      return;
    }

    const query = String(candidateLookupInputValue || "").trim();
    if (query.length < 2) {
      setCandidateLookupOptions(
        selectedExistingCandidate?.id ? [selectedExistingCandidate] : []
      );
      setCandidateLookupLoading(false);
      setCandidateLookupDebug("");
      return;
    }

    if (
      selectedExistingCandidate?.id &&
      normalizeCompanyName(selectedExistingCandidate.full_name).toLowerCase() ===
        normalizeCompanyName(query).toLowerCase()
    ) {
      setCandidateLookupOptions([selectedExistingCandidate]);
      setCandidateLookupLoading(false);
      setCandidateLookupDebug("");
      return;
    }

    let active = true;
    const cacheKey = query.toLowerCase();
    const cachedOptions = candidateSearchCacheRef.current.get(cacheKey);
    if (cachedOptions) {
      setCandidateLookupOptions(cachedOptions);
      setCandidateLookupLoading(false);
      setCandidateLookupDebug(
        cachedOptions.length ? "" : "No matching candidates found. Keep typing to create a new one."
      );
      return undefined;
    }
    const timeoutId = window.setTimeout(async () => {
      setCandidateLookupLoading(true);
      setCandidateLookupDebug("");

      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "20",
          sort: "updated_at",
          order: "desc",
          search: query,
        });
        const response = await fetch(`${apiBase}/api/candidates?${params.toString()}`, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: authToken.startsWith("Bearer ")
              ? authToken
              : `Bearer ${authToken}`,
            "x-access-token": authToken.replace(/^Bearer\s+/i, ""),
          },
        });

        const payload = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(
            payload?.error ||
              payload?.message ||
              `Failed to search candidates (${response.status})`
          );
        }

        const nextOptions = Array.from(
          new Map(
            (Array.isArray(payload?.rows) ? payload.rows : [])
              .map((row) => normalizeExistingCandidateOption(row))
              .filter(
                (row) =>
                  row?.id != null && Number.isFinite(Number(row.id)) && Number(row.id) > 0
              )
              .map((row) => [String(row.id), row])
          ).values()
        );

        if (!active) return;
        candidateSearchCacheRef.current.set(cacheKey, nextOptions);
        setCandidateLookupOptions(nextOptions);
        setCandidateLookupDebug(
          nextOptions.length ? "" : "No matching candidates found. Keep typing to create a new one."
        );
      } catch (error) {
        if (!active) return;
        setCandidateLookupOptions([]);
        setCandidateLookupDebug(error?.message || "Failed to search candidates");
      } finally {
        if (active) {
          setCandidateLookupLoading(false);
        }
      }
    }, 75);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    apiBase,
    candidateLookupInputValue,
    getToken,
    open,
    screen,
    selectedExistingCandidate,
  ]);

  const candidateLookupHelperText = useMemo(() => {
    if (selectedExistingCandidate?.id) {
      if (shouldRestrictToExistingCandidate) {
        return shouldShowOverviewStageField
          ? "Existing candidate selected. Click Add Candidate to assign this candidate and set the stage."
          : "Existing candidate selected. Click Add to Job to assign this candidate.";
      }

      return showJobStageField
        ? "Existing candidate selected. Changing the stage will add this candidate to the job; editing other fields will create a new candidate."
        : "Existing candidate selected. Editing the fields will create a new candidate.";
    }

    if (candidateLookupLoading) {
      return "Searching candidates...";
    }

    if (candidateLookupDebug) {
      return candidateLookupDebug;
    }

    if (shouldRestrictToExistingCandidate) {
      return shouldShowOverviewStageField
        ? "Search by first name or last name, then choose the stage for this job."
        : "Search by first name or last name to add an existing candidate to this job.";
    }

    return "Search by first name or last name. Select a candidate to autofill the form.";
  }, [
    candidateLookupDebug,
    candidateLookupLoading,
    selectedExistingCandidate?.id,
    shouldShowOverviewStageField,
    showJobStageField,
    shouldRestrictToExistingCandidate,
  ]);

  const selectedCompany = useMemo(() => {
    const byId = companies.find((c) => String(c.id) === String(form.company_client_id));
    if (byId) return byId;

    const typed = normalizeCompanyName(form.company_client);
    if (!typed) return null;

    const byName = companies.find((c) => c.name.toLowerCase() === typed.toLowerCase());
    if (byName) return byName;

    return {
      id: null,
      name: typed,
      logo: form.company_client_logo || "",
    };
  }, [companies, form.company_client, form.company_client_id, form.company_client_logo]);

  const selectedCompanyId = useMemo(() => {
    const value = Number(selectedCompany?.id ?? form.company_client_id ?? 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [form.company_client_id, selectedCompany?.id]);

  const loadCompanyContacts = useCallback(
    async (companyId) => {
      const normalizedCompanyId = Number(companyId);
      if (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0) {
        setCompanyContacts([]);
        setCompanyContactsDebug("");
        setCompanyContactsLoading(false);
        return [];
      }

      setCompanyContactsLoading(true);
      setCompanyContactsDebug("");

      try {
        const rows = await fetchCompanyContactOptions({
          apiBase,
          companyId: normalizedCompanyId,
          token: getToken(),
        });
        setCompanyContacts(rows);
        setCompanyContactsDebug(rows.length ? "" : "No company contacts found");
        return rows;
      } catch (error) {
        setCompanyContacts([]);
        setCompanyContactsDebug(error?.message || "Failed to load company contacts");
        return [];
      } finally {
        setCompanyContactsLoading(false);
      }
    },
    [apiBase, getToken]
  );

  useEffect(() => {
    if (!open) return;
    if (!selectedCompanyId) {
      setCompanyContacts([]);
      setCompanyContactsDebug("");
      setCompanyContactsLoading(false);
      return;
    }

    loadCompanyContacts(selectedCompanyId);
  }, [loadCompanyContacts, open, selectedCompanyId]);

  const assignTeamOptions = useMemo(
    () => (selectedCompanyId ? companyContacts : teamMembers),
    [companyContacts, selectedCompanyId, teamMembers]
  );

  const assignTeamLoading = selectedCompanyId ? companyContactsLoading : teamMembersLoading;
  const assignTeamDebug = selectedCompanyId ? companyContactsDebug : teamMembersDebug;

  useEffect(() => {
    setErrors((prev) => {
      if (!prev || typeof prev !== "object" || !Object.keys(prev).length) {
        return prev;
      }

      if (selectedExistingCandidate?.id) {
        return {};
      }

      let changed = false;
      const next = { ...prev };
      const clearIfResolved = (key, resolved) => {
        if (resolved && Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
      };

      clearIfResolved("full_name", Boolean(String(form.full_name || "").trim()));
      clearIfResolved("email", Boolean(String(form.email || "").trim()));
      clearIfResolved("phone", Boolean(String(form.phone || "").trim()));
      clearIfResolved("location", Boolean(String(form.location || "").trim()));
      clearIfResolved("title", Boolean(String(form.title || "").trim()));
      clearIfResolved(
        "stage",
        !showJobStageField || Boolean(String(form.stage || "").trim())
      );
      clearIfResolved(
        "company_client",
        Boolean(normalizeCompanyName(form.company_client || ""))
      );
      clearIfResolved("source", Boolean(String(form.source || "").trim()));

      if (changed && Object.prototype.hasOwnProperty.call(next, "_form")) {
        delete next._form;
      }

      return changed ? next : prev;
    });
  }, [
    form.company_client,
    form.email,
    form.full_name,
    form.location,
    form.phone,
    form.source,
    form.stage,
    form.title,
    selectedExistingCandidate?.id,
    showJobStageField,
  ]);

  const selectedTeamMember = useMemo(() => {
    const typed = normalizeTeamMemberName(form.assigned_team_member || form.team_name || "");
    if (!typed) return null;

    const byName = assignTeamOptions.find((item) => item.name.toLowerCase() === typed.toLowerCase());
    if (byName) return byName;

    return {
      id: null,
      name: typed,
    };
  }, [assignTeamOptions, form.assigned_team_member, form.team_name]);

  const candidateLinkSummary = useMemo(() => {
    const summary = describeLinkAutofill(getCandidateLinkAutofill(candidateLinkInput), {
      includeProfile: true,
    });
    return summary || "Autofills source and candidate details when detected.";
  }, [candidateLinkInput]);

  const uploadParsedCompanyName = useMemo(
    () =>
      normalizeCompanyName(
        form.company_client ||
          companyInputValue ||
          resumeAiData.company_client ||
          resumeAiData.current_company ||
          inferCurrentCompanyFromExperienceItems(normalizeAiArray(resumeAiData.experience_items)) ||
          ""
      ),
    [
      companyInputValue,
      form.company_client,
      resumeAiData.company_client,
      resumeAiData.current_company,
      resumeAiData.experience_items,
    ]
  );

  const uploadNeedsCompanyTableEntry = useMemo(
    () => Boolean(uploadParsedCompanyName) && !selectedCompany?.id,
    [selectedCompany?.id, uploadParsedCompanyName]
  );

  const showUploadBackgroundTasks = useMemo(
    () =>
      screen === "upload" &&
      resumeFile instanceof File &&
      (uploadAutoCreateStage === "queued" || uploadAutoCreateStage === "saving"),
    [resumeFile, screen, uploadAutoCreateStage]
  );

  const uploadBackgroundTasks = useMemo(() => {
    if (!showUploadBackgroundTasks) return [];

    const workStatus = uploadAutoCreateStage === "saving" ? "working" : "queued";
    const tasks = [
      {
        key: "resumeParsed",
        label: "Resume parsed and autofill prepared",
        status: "done",
      },
      {
        key: "candidate",
        label: "Create the candidate record",
        status: workStatus,
      },
      {
        key: "resumeSave",
        label: "Save the resume and parsed experience/education",
        status: workStatus,
      },
    ];

    if (uploadParsedCompanyName) {
      tasks.push(
        uploadNeedsCompanyTableEntry
          ? {
              key: "companyCreate",
              label: `Add "${uploadParsedCompanyName}" to the Companies table view`,
              status: workStatus,
            }
          : {
              key: "companyLink",
              label: `Link the candidate to ${uploadParsedCompanyName}`,
              status: workStatus,
            }
      );
    }

    return tasks;
  }, [
    showUploadBackgroundTasks,
    uploadAutoCreateStage,
    uploadNeedsCompanyTableEntry,
    uploadParsedCompanyName,
  ]);

  const setAssignedTeamMemberValue = useCallback((value) => {
    const nextValue = normalizeTeamMemberName(value);
    setForm((p) => ({
      ...p,
      assigned_team_member: nextValue,
      team_name: nextValue,
    }));
    setErrors((p) => {
      const next = { ...p };
      delete next.assigned_team_member;
      return next;
    });
  }, []);

  const shouldPreserveExistingCandidateSelection = useCallback(
    (fieldKey) => {
      if (!selectedExistingCandidate?.id) return false;
      return fieldKey === "stage" && showJobStageField;
    },
    [selectedExistingCandidate?.id, showJobStageField]
  );

  const ensureAssignedTeamMemberValue = useCallback(
    async (value, authToken) => {
      const normalizedValue = normalizeTeamMemberName(value);
      if (!normalizedValue) return "";

      const matchingCompanyContact = selectedCompanyId
        ? companyContacts.find(
            (item) => item.name.toLowerCase() === normalizedValue.toLowerCase()
          )
        : null;
      if (matchingCompanyContact?.name) {
        return matchingCompanyContact.name;
      }

      const ensuredTeamMember = await createTeamMemberIfMissing({
        apiBase,
        token: authToken,
        name: normalizedValue,
        existing: teamMembers,
      });
      const finalValue = ensuredTeamMember?.name || normalizedValue;
      setTeamMembers((prev) =>
        upsertTeamMemberOption(prev, ensuredTeamMember || { name: finalValue })
      );
      return finalValue;
    },
    [apiBase, companyContacts, selectedCompanyId, teamMembers]
  );

  const setField = (key) => (e) => {
    const value = e.target.value;
    if (
      selectedExistingCandidate?.id &&
      !shouldPreserveExistingCandidateSelection(key)
    ) {
      clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
    }

    if (key === "website_url") {
      const nextWebsite = deriveWebsiteValue("", value);
      setForm((p) => ({ ...p, website_url: value, website: nextWebsite || p.website || "" }));
    } else if (key === "work_authorization") {
      setForm((p) => ({
        ...p,
        work_authorization: value,
        work_auhtorization: value,
      }));
    } else if (key === "assigned_team_member") {
      setAssignedTeamMemberValue(value);
    } else {
      setForm((p) => ({ ...p, [key]: value }));
    }

    setErrors((p) => {
      const next = { ...p };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  const applyCandidateLinkAutofill = useCallback(
    (rawValue) => {
      const nextRawValue = String(rawValue || "");
      const autofill = getCandidateLinkAutofill(nextRawValue);
      const nextCompanyName = String(autofill.companyName || "").trim();

      setCandidateLinkInput(nextRawValue);
      setForm((prev) => {
        const next = {
          ...prev,
          linkedin_url: "",
          website_url: "",
          other_url: "",
        };

        if (autofill.normalizedUrl) {
          if (autofill.isResumeDocument) next.other_url = autofill.normalizedUrl;
          else if (autofill.source === "LinkedIn") next.linkedin_url = autofill.normalizedUrl;
          else if (autofill.source === "Website") next.website_url = autofill.normalizedUrl;
          else next.other_url = autofill.normalizedUrl;
        }

        if (autofill.source) next.source = autofill.source;
        if (autofill.website && !autofill.isResumeDocument) {
          next.website = autofill.website;
          if (!next.website_url) next.website_url = autofill.website;
        }

        if (nextCompanyName.length > 1) {
          next.company_client = nextCompanyName;
          next.company_client_id = null;
          next.company_client_logo = "";
        }

        return next;
      });

      if (nextCompanyName.length > 1) {
        setCompanyInputValue(nextCompanyName);
      }

      setErrors((prev) => {
        const next = { ...prev };
        delete next.company_client;
        delete next.linkedin_url;
        return next;
      });

      return autofill;
    },
    []
  );

  const handleLinkCompanyChange = useCallback(
    (e) => {
      const nextValue = e?.target?.value || "";
      if (selectedExistingCandidate?.id) {
        clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
      }
      const resolved = resolveCompanyValue(nextValue, companies);

      setCompanyInputValue(nextValue);
      setForm((p) => ({
        ...p,
        company_client: resolved.company_client,
        company_client_id: resolved.company_client_id,
        company_client_logo: resolved.company_client_logo,
      }));
      setErrors((p) => {
        const next = { ...p };
        delete next.company_client;
        return next;
      });
    },
    [clearSelectedExistingCandidate, companies, firstName, form.full_name, lastName, selectedExistingCandidate?.id]
  );

  const handleCandidateStartLinkChange = useCallback(
    (e) => {
      applyCandidateLinkAutofill(e?.target?.value || "");
    },
    [applyCandidateLinkAutofill]
  );

  const handleContinueFromLink = useCallback(async () => {
    const rawLinkAutofill = deriveLinkAutofill(candidateLinkInput, "candidate");
    const linkAutofill = getCandidateLinkAutofill(candidateLinkInput);
    const authToken = getToken();
    if (!authToken) {
      setToast({ open: true, type: "error", msg: "No auth token found. Please login again." });
      return;
    }

    try {
      setStartedFromLinkFlow(true);
      setCandidateImportMode("link");
      setCreatingLinkCompany(true);
      const normalizedLink = safeUrl(candidateLinkInput) || linkAutofill.normalizedUrl || "";
      let parsedLink = null;

      if (normalizedLink) {
        try {
          parsedLink = await parseCandidateLinkWithOpenAI({
            authToken,
            url: normalizedLink,
          });
        } catch (error) {
          console.error("AddCandidateDialog candidate link parse failed:", error);
        }
      }

      const shouldTreatLinkAsResume = Boolean(
        linkAutofill.isResumeDocument || isResumeDocumentLinkKind(parsedLink?.link_kind)
      );

      const parsedExperienceItems = normalizeAiArray(parsedLink?.experience_items);
      const parsedEducationItems = normalizeAiArray(parsedLink?.education_items);
      let nextResumeAiData = mergeResumeAiData(
        normalizeCompanyAiProfile(parsedLink),
        {
          experience_items: parsedExperienceItems,
          education_items: parsedEducationItems,
        }
      );
      const parsedCompanyName = normalizeCompanyName(
        nextResumeAiData.company_client ||
          nextResumeAiData.current_company ||
          inferCurrentCompanyFromExperienceItems(parsedExperienceItems)
      );

      nextResumeAiData = mergeResumeAiData(nextResumeAiData, {
        company_client: parsedCompanyName,
        current_company: parsedCompanyName,
      });
      const resolvedParsedCompany = parsedCompanyName
        ? resolveCompanyValue(parsedCompanyName, companies)
        : null;

      if (parsedLink) {
        const parsedFullName = normalizeExtractedFullName(parsedLink.full_name || "");
        if (isStrongCandidateFullName(parsedFullName)) {
          const nameParts = splitName(parsedFullName);
          setFirstName(nameParts.first);
          setLastName(nameParts.last);
          setCandidateLookupInputValue(parsedFullName);
        }
        setForm((prev) => {
          const nextBase = { ...prev };
          const normalizedPreviousWebsiteUrl = safeUrl(prev.website_url) || "";
          const normalizedPreviousOtherUrl = safeUrl(prev.other_url) || "";
          const rawDocumentWebsite = String(
            rawLinkAutofill.website || linkAutofill.documentHostWebsite || ""
          ).trim();

          if (shouldTreatLinkAsResume) {
            if (normalizedLink && normalizedPreviousWebsiteUrl === normalizedLink) {
              nextBase.website_url = "";
            }
            if (rawDocumentWebsite && String(prev.website || "").trim() === rawDocumentWebsite) {
              nextBase.website = "";
            }
            if (normalizedLink && !normalizedPreviousOtherUrl) {
              nextBase.other_url = normalizedLink;
            }
          }

          const merged = mergeAutofill(nextBase, parsedLink, {
            preferExtractedFullName: true,
          });
          const existingCompanyName = normalizeCompanyName(prev.company_client || "");
          const heuristicCompanyName = normalizeCompanyName(
            shouldTreatLinkAsResume ? "" : linkAutofill.companyName || ""
          );
          const parsedCompanyLabel =
            resolvedParsedCompany?.company_client || parsedCompanyName || "";
          const shouldApplyParsedCompany =
            parsedCompanyLabel &&
            (
              !existingCompanyName ||
              (!prev.company_client_id &&
                heuristicCompanyName &&
                existingCompanyName.toLowerCase() === heuristicCompanyName.toLowerCase())
            );

          if (shouldTreatLinkAsResume) {
            if (normalizedLink && (safeUrl(merged.website_url) || "") === normalizedLink) {
              merged.website_url = "";
            }
            if (
              rawDocumentWebsite &&
              String(merged.website || "").trim() === rawDocumentWebsite &&
              !String(parsedLink?.website || "").trim()
            ) {
              merged.website = "";
            }
            if (normalizedLink && !String(merged.other_url || "").trim()) {
              merged.other_url = normalizedLink;
            }
          }

          if (shouldApplyParsedCompany) {
            merged.company_client = parsedCompanyLabel;
            merged.company_client_id = resolvedParsedCompany?.company_client_id ?? null;
            merged.company_client_logo = resolvedParsedCompany?.company_client_logo || "";
          }

          return merged;
        });
        setResumeAiData(nextResumeAiData);

        const parsedCompanyLabel =
          resolvedParsedCompany?.company_client || parsedCompanyName || "";

        if (parsedCompanyLabel) {
          setCompanyInputValue(
            (prev) => {
              const existingValue = normalizeCompanyName(prev);
              const heuristicValue = normalizeCompanyName(linkAutofill.companyName || "");
              if (!existingValue) return parsedCompanyLabel;
              if (
                heuristicValue &&
                existingValue.toLowerCase() === heuristicValue.toLowerCase()
              ) {
                return parsedCompanyLabel;
              }
              return prev;
            }
          );
        }

        const parsedFields = [];
        if (parsedLink.full_name) parsedFields.push("full_name");
        if (parsedLink.title) parsedFields.push("title");
        if (parsedLink.location) parsedFields.push("location");
        if (parsedLink.email) parsedFields.push("email");
        if (parsedLink.phone) parsedFields.push("phone");
        if (parsedLink.linkedin_url || parsedLink.website_url || parsedLink.social_links?.length) {
          parsedFields.push("social links");
        }
        if (parsedExperienceItems.length) parsedFields.push("experience_items");
        if (parsedEducationItems.length) parsedFields.push("education_items");
        if (parsedCompanyLabel) parsedFields.push("company_client");

        if (parsedLink.parse_warning) {
          setToast({
            open: true,
            type: "info",
            msg: parsedLink.parse_warning,
          });
        } else if (parsedFields.length) {
          setToast({
            open: true,
            type: "success",
            msg: `AI parsed from link: ${parsedFields.join(", ")}`,
          });
        }
      }

      const rawHeuristicCompanyName = normalizeCompanyName(
        rawLinkAutofill.companyName || linkAutofill.documentHostCompanyName || ""
      );
      const typedOrAutofilledCompanyRaw = normalizeCompanyName(
        companyInputValue || form.company_client || selectedCompany?.name || ""
      );
      const typedOrAutofilledCompany =
        shouldTreatLinkAsResume &&
        rawHeuristicCompanyName &&
        typedOrAutofilledCompanyRaw.toLowerCase() === rawHeuristicCompanyName.toLowerCase()
          ? ""
          : typedOrAutofilledCompanyRaw;
      const heuristicCompanyName = normalizeCompanyName(
        shouldTreatLinkAsResume ? "" : linkAutofill.companyName || ""
      );
      const companyName = normalizeCompanyName(
        (!selectedCompany?.id &&
        parsedCompanyName &&
        (
          !typedOrAutofilledCompany ||
          (heuristicCompanyName &&
            typedOrAutofilledCompany.toLowerCase() === heuristicCompanyName.toLowerCase())
        )
          ? parsedCompanyName
          : typedOrAutofilledCompany) ||
          resolvedParsedCompany?.company_client ||
          parsedCompanyName ||
          heuristicCompanyName ||
          ""
      );

      if (companyName) {
        const resolvedCompany = resolveCompanyValue(companyName, companies);
        const nextCompanyClient = resolvedCompany.company_client || companyName;

        setForm((p) => ({
          ...p,
          company_client: nextCompanyClient,
          company_client_id: resolvedCompany.company_client_id ?? null,
          company_client_logo: resolvedCompany.company_client_logo || "",
        }));
        setCompanyInputValue(nextCompanyClient);
        setErrors((p) => {
          const next = { ...p };
          delete next.company_client;
          return next;
        });
      }
      setScreen("overview");
    } catch (error) {
      const message = error?.message || "Failed to process candidate link";
      setErrors((p) => ({ ...p, company_client: message }));
      setToast({ open: true, type: "error", msg: message });
    } finally {
      setCreatingLinkCompany(false);
    }
  }, [
    candidateLinkInput,
    companyInputValue,
    companies,
    form.company_client,
    getToken,
    parseCandidateLinkWithOpenAI,
    selectedCompany?.id,
    selectedCompany?.name,
  ]);

  const handleCandidateStartLinkContinue = useCallback(async () => {
    applyCandidateLinkAutofill(candidateLinkInput);
    await handleContinueFromLink();
  }, [applyCandidateLinkAutofill, candidateLinkInput, handleContinueFromLink]);

  const setFirst = (e) => {
    const v = e.target.value;
    const nextFullName = joinName(v, lastName);
    if (selectedExistingCandidate?.id) {
      clearSelectedExistingCandidate(nextFullName);
    }
    setFirstName(v);
    setCandidateLookupInputValue(nextFullName);
    setForm((p) => ({ ...p, full_name: nextFullName }));
    setErrors((p) => {
      const next = { ...p };
      delete next.full_name;
      return next;
    });
  };

  const setLast = (e) => {
    const v = e.target.value;
    const nextFullName = joinName(firstName, v);
    if (selectedExistingCandidate?.id) {
      clearSelectedExistingCandidate(nextFullName);
    }
    setLastName(v);
    setCandidateLookupInputValue(nextFullName);
    setForm((p) => ({ ...p, full_name: nextFullName }));
    setErrors((p) => {
      const next = { ...p };
      delete next.full_name;
      return next;
    });
  };

  const handleCandidateLookupInputChange = useCallback(
    (_, nextInputValue, reason) => {
      if (reason === "reset") {
        setCandidateLookupInputValue(
          selectedExistingCandidate?.full_name || String(nextInputValue || "")
        );
        return;
      }

      const nextValue = String(nextInputValue || "");
      if (reason === "clear") {
        clearSelectedExistingCandidate("");
        return;
      }

      if (selectedExistingCandidate?.id) {
        clearSelectedExistingCandidate(nextValue);
      } else {
        setCandidateLookupInputValue(nextValue);
      }
    },
    [clearSelectedExistingCandidate, selectedExistingCandidate]
  );

  const handleCandidateLookupChange = useCallback(
    (_, newValue) => {
      if (!newValue) {
        clearSelectedExistingCandidate("");
        return;
      }

      if (newValue?.createOption) {
        const typedFullName = String(
          newValue.full_name || newValue.inputValue || candidateLookupInputValue || ""
        ).trim();
        const nextNameParts = splitName(typedFullName);
        const nextBaseForm = selectedExistingCandidate?.id
          ? {
              ...DEFAULT_FORM,
              owner: ownerName,
            }
          : null;

        setSelectedExistingCandidate(null);
        setCandidateLookupInputValue(typedFullName);
        setCandidateLookupOptions([]);
        setCandidateLookupDebug("");
        setResumeAiData(EMPTY_RESUME_AI);
        parsedResumeTextRef.current = "";
        resumeAiParseCompleteRef.current = false;
        pendingUploadAutoSubmitRef.current = false;
        setResumeFile(null);
        setAvatarFile(null);
        if (avatarPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(avatarPreview);
        }
        setAvatarPreview("");
        setCandidateLinkInput("");
        setCompanyInputValue("");
        setForm((prev) => ({
          ...(nextBaseForm || prev),
          full_name: typedFullName,
        }));
        setFirstName(nextNameParts.first);
        setLastName(nextNameParts.last);
        setErrors((prev) => {
          const next = { ...prev };
          delete next.full_name;
          return next;
        });
        return;
      }

      const hydrated = buildDialogStateFromExistingCandidate(newValue, ownerName);
      setSelectedExistingCandidate(hydrated.candidate);
      setCandidateLookupInputValue(hydrated.candidateLookupInputValue);
      setCandidateLookupOptions([hydrated.candidate]);
      setCandidateLookupDebug("");
      setForm(hydrated.form);
      setFirstName(hydrated.firstName);
      setLastName(hydrated.lastName);
      setCompanyInputValue(hydrated.companyInputValue);
      setCandidateLinkInput(hydrated.candidateLinkInput);
      setResumeAiData(hydrated.resumeAiData);
      parsedResumeTextRef.current = "";
      pendingUploadAutoSubmitRef.current = false;
      setResumeFile(null);
      setAvatarFile(null);
      if (avatarPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(avatarPreview);
      }
      setAvatarPreview(hydrated.avatarPreview);
      setErrors({});
    },
    [
      avatarPreview,
      candidateLookupInputValue,
      clearSelectedExistingCandidate,
      ownerName,
      selectedExistingCandidate?.id,
    ]
  );

  const handlePickResume = useCallback(async (e) => {
    const file = e.target.files?.[0] || null;
    if (selectedExistingCandidate?.id) {
      clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
    }
    setResumeFile(file);
    setResumeAiData(EMPTY_RESUME_AI);
    setExtractedResumeCompanies([]);
    setSelectedPrimaryCompanies([]);
    setPrimaryCompanyPickerOpen(false);
    parsedResumeTextRef.current = "";
    resumeAiParseCompleteRef.current = false;
    pendingUploadAutoSubmitRef.current = false;
    pendingResumeBackgroundParseRef.current = false;
    setCandidateImportMode(file ? "resume" : "");
    setUploadAutoCreateStage(file ? "parsing" : "idle");

    if (!file) return;

    const name = String(file.name || "").toLowerCase();
    const type = String(file.type || "").toLowerCase();

      setParsingResume(true);
      writeCandidateImportWork({
        status: "parsing",
        message: "Parsing the candidate resume and identifying experience companies. View Activities for progress.",
      });
      setToast({
        open: true,
        type: "info",
        msg: "Parsing the resume and identifying experience companies…",
      });
    try {
      let text = "";
      let localExtracted = {};
      let extractedAvatar = null;
      let extractedCompanies = [];

      const isPdf = type.includes("pdf") || name.endsWith(".pdf");
      const isDocx =
        type.includes("officedocument.wordprocessingml.document") || name.endsWith(".docx");
      const isDoc = type.includes("msword") || name.endsWith(".doc");
      const isRtf = type.includes("rtf") || name.endsWith(".rtf");
      const isText = type.startsWith("text/plain") || name.endsWith(".txt");
      const isOdt = type.includes("opendocument.text") || name.endsWith(".odt");
      const isHtml = type.includes("text/html") || name.endsWith(".html") || name.endsWith(".htm");
      const isImage = type.startsWith("image/") || /\.(?:jpe?g|png|webp)$/i.test(name);

      if (!isPdf && !isDocx && !isDoc && !isRtf && !isText && !isOdt && !isHtml && !isImage) {
        setUploadAutoCreateStage("idle");
        setToast({
          open: true,
          type: "error",
          msg: "Unsupported resume format. Please upload a document, text file, or resume image.",
        });
        return;
      }

      try {
        if (isPdf) text = await parsePdfToText(file);
        else if (isDocx) text = await parseDocxToText(file);
        else if (isText || isRtf || isHtml) text = await file.text();
      } catch {
        text = "";
      }

      if (isPdf && !avatarFile && !avatarPreview) {
        try {
          extractedAvatar = await extractPdfProfilePhoto(file);
          if (extractedAvatar) {
            setAvatarFile(extractedAvatar);
            setAvatarPreview(URL.createObjectURL(extractedAvatar));
          }
        } catch {
          extractedAvatar = null;
        }
      }

      parsedResumeTextRef.current = text;

      if (text) {
        localExtracted = extractCandidateFromText(text);
        setForm((prev) => mergeAutofill(prev, localExtracted));
      }

      const authToken = getToken();
      if (authToken) {
        const parseForm = new FormData();
        parseForm.append("resume", file);
        if (text) parseForm.append("resume_text", text);
        const response = await fetch(`${apiBase}/api/candidates/parse-resume`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${authToken}` },
          body: parseForm,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result?.error || "Unable to extract companies from the CV.");
        }

        const parsedResume = mergeResumeAiData(EMPTY_RESUME_AI, result?.parsed || {});
        // The dedicated upload step has completed the server-side AI parse,
        // including for scanned/image resumes with no browser text layer.
        // Preserve that fact so candidate creation does not parse the same
        // file a second time in the background.
        resumeAiParseCompleteRef.current = true;
        extractedCompanies = getExtractedResumeCompanies(parsedResume);
        const aiFullName = normalizeExtractedFullName(parsedResume.full_name || "");
        setResumeAiData(parsedResume);
        setExtractedResumeCompanies(extractedCompanies);
        // The server-side AI parse is more reliable than browser text
        // heuristics, especially for multi-column CVs. Let it replace the
        // provisional local values before the candidate is saved.
        setForm((prev) =>
          mergeAutofill(prev, result?.parsed || localExtracted, {
            preferAiFields: true,
            preferAiTitle: true,
            preferExtractedFullName: true,
          })
        );
        if (isStrongCandidateFullName(aiFullName)) {
          const nameParts = splitName(aiFullName);
          setFirstName(nameParts.first);
          setLastName(nameParts.last);
          setCandidateLookupInputValue(aiFullName);
        }
        if (extractedCompanies.length) {
          // The recruiter must explicitly choose the one company that may
          // enter Contact and Lead research. Keep the overview visible first
          // so the optional Job and required Candidate Stage can be reviewed before
          // the primary-company confirmation is shown at final creation.
          setSelectedPrimaryCompanies([]);
          setCandidateImportHandoff(false);
          clearCandidateImportWork();
        } else {
          // There is no primary-company decision to make, so return to the
          // normal form for the recruiter to complete the import manually.
          setCandidateImportHandoff(false);
          clearCandidateImportWork();
        }
      }

      pendingResumeBackgroundParseRef.current = true;
      // A CV import must be reviewed with its optional Job and required stage before the
      // candidate is created. Do not auto-submit parsed resumes.
      pendingUploadAutoSubmitRef.current = false;
      setUploadAutoCreateStage("idle");

      setToast({
        open: true,
        type: "info",
        msg: extractedCompanies.length
          ? "Resume parsed. Optionally select a job and candidate stage, then choose the companies to research."
          : "Resume parsed. Review the candidate details before creating.",
      });
      setScreen("overview");
    } catch {
      setUploadAutoCreateStage("idle");
      setCandidateImportHandoff(false);
      clearCandidateImportWork();
      setToast({
        open: true,
        type: "error",
        msg: "Failed to parse resume.",
      });
    } finally {
      setParsingResume(false);
      try {
        e.target.value = "";
      } catch {}
    }
  }, [
    clearSelectedExistingCandidate,
    avatarFile,
    avatarPreview,
    firstName,
    form.full_name,
    lastName,
    selectedExistingCandidate?.id,
    apiBase,
    getToken,
  ]);

  const handlePickAvatar = (e) => {
    const file = e.target.files?.[0] || null;
    if (selectedExistingCandidate?.id) {
      clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
    }
    setAvatarFile(file);

    if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(file ? URL.createObjectURL(file) : "");
  };

  const clearResume = () => {
    setResumeFile(null);
    setCandidateImportMode("");
    setResumeAiData(EMPTY_RESUME_AI);
    setExtractedResumeCompanies([]);
    setSelectedPrimaryCompanies([]);
    setPrimaryCompanyPickerOpen(false);
    clearCandidateImportWork();
    parsedResumeTextRef.current = "";
    resumeAiParseCompleteRef.current = false;
    pendingUploadAutoSubmitRef.current = false;
    setUploadAutoCreateStage("idle");
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview("");
  };

  const handlePickResumeFromStart = useCallback(
    async (e) => {
      setStartedFromLinkFlow(false);
      // Keep the dialog visible throughout resume parsing so the recruiter
      // can see progress and review the extracted details without the modal
      // disappearing underneath them.
      setCandidateImportHandoff(false);
      setScreen("overview");
      await handlePickResume(e);
    },
    [handlePickResume]
  );

  const buildCandidateValidationErrors = useCallback(
    (scope = "all", formValue = form) => {
      if (selectedExistingCandidate?.id) {
        return {};
      }

      const next = {};
      const missing = [];
      const includeOverview = scope === "overview" || scope === "all";
      const includeProfessional = scope === "professional" || scope === "all";

      if (includeOverview) {
        if (!String(formValue.full_name || "").trim()) {
          next.full_name = "Name is required";
          missing.push("Name");
        }

        if (!String(formValue.email || "").trim()) {
          next.email = "Email is required";
          missing.push("Email");
        }

        if (!String(formValue.phone || "").trim()) {
          next.phone = "Phone is required";
          missing.push("Phone");
        }

        if (!String(formValue.location || "").trim()) {
          next.location = "Location is required";
          missing.push("Location");
        }

        if (!String(formValue.title || "").trim()) {
          next.title = "Primary Role / Expertise is required";
          missing.push("Primary Role / Expertise");
        }

        if ((showJobStageField || requiresJobAndStageSelection) && !String(formValue.stage || "").trim()) {
          next.stage = "Stage is required";
          missing.push("Stage");
        }

      }

      if (includeProfessional) {
        if (!String(formValue.source || "").trim()) {
          next.source = "Source is required";
          missing.push("Source");
        }

        const ln = safeUrl(formValue.linkedin_url);
        if (String(formValue.linkedin_url || "").trim() && ln === null) {
          next.linkedin_url = "Enter a valid URL";
        }

        const web = safeUrl(formValue.website_url);
        if (String(formValue.website_url || "").trim() && web === null) {
          next.website_url = "Enter a valid URL";
        }

        const other = safeUrl(formValue.other_url);
        if (String(formValue.other_url || "").trim() && other === null) {
          next.other_url = "Enter a valid URL";
        }
      }

      if (missing.length) {
        next._form = formatMissingRequiredFieldsMessage(missing);
      }

      return next;
    },
    [form, requiresJobAndStageSelection, selectedExistingCandidate?.id, showJobStageField]
  );

  const validateOverview = useCallback(
    (formValue = form) => {
      const next = buildCandidateValidationErrors("overview", formValue);
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [buildCandidateValidationErrors, form]
  );

  const validateProfessional = useCallback(
    (formValue = form) => {
      const next = buildCandidateValidationErrors("professional", formValue);
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [buildCandidateValidationErrors, form]
  );

  const handleClose = () => {
    if (!saving) onClose?.();
  };

  const redirectToCreatedCandidate = useCallback(
    (candidate, extraState = null) => {
      const candidateId = candidate?.candidate_id ?? candidate?.id ?? null;
      if (!candidateId) return;

      const search = new URLSearchParams({
        candidateId: String(candidateId),
        tab: "details",
      }).toString();

      navigate(
        {
          pathname: "/candidates",
          search: `?${search}`,
        },
        {
          replace: false,
          state: {
            createdCandidate: candidate,
            createdCandidateId: candidateId,
            source: "add-candidate-dialog",
            ...(extraState && typeof extraState === "object" ? extraState : {}),
          },
        }
      );
    },
    [navigate]
  );

  const redirectToActivities = useCallback(
    (candidate, message = "") => {
      const candidateId = candidate?.candidate_id ?? candidate?.id ?? null;
      navigate("/activities", {
        replace: false,
        state: {
          source: "add-candidate-resume",
          candidateId,
          actionToast: {
            message: message || "Candidate created. Resume parsing is now running in Activities.",
            severity: "info",
          },
        },
      });
    },
    [navigate]
  );

  const closeDialogBeforeNavigation = useCallback(async () => {
    onClose?.();

    // Let the dialog/backdrop fully detach before we change routes.
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(fallbackTimer);
        resolve();
      };
      const fallbackTimer = window.setTimeout(finish, 100);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(finish);
      });
    });
  }, [onClose]);

  const postCandidate = useCallback(
    async (authToken, payload, resumeFileArg, avatarFileArg) => {
      const urls = [`${apiBase}/api/candidates`, `${apiBase}/candidates`];
      let lastError = null;

      for (const url of urls) {
        try {
          if (!(resumeFileArg instanceof File) && !(avatarFileArg instanceof File)) {
            const { res, data } = await requestJson({
              url,
              method: "POST",
              token: authToken,
              body: payload,
            });

            if (res.ok) return data;
            lastError = new Error(data?.error || data?.message || `Failed to create candidate (${res.status})`);
            continue;
          }

          const fd = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value == null) {
              fd.append(key, "");
              return;
            }
            if (typeof value === "object") {
              fd.append(key, JSON.stringify(value));
              return;
            }
            fd.append(key, String(value));
          });

          if (resumeFileArg instanceof File) fd.append("resume", resumeFileArg);
          if (avatarFileArg instanceof File) fd.append("avatar", avatarFileArg);

          const { res, data } = await requestJson({
            url,
            method: "POST",
            token: authToken,
            body: fd,
            isFormData: true,
          });

          if (res.ok) return data;
          lastError = new Error(data?.error || data?.message || `Failed to create candidate (${res.status})`);
        } catch (err) {
          lastError = err;
        }
      }

      throw lastError || new Error("Failed to create candidate");
    },
    [apiBase]
  );

  const updateCandidateProfile = useCallback(
    async (authToken, candidateId, profilePatch) => {
      if (!authToken) {
        throw new Error("Missing auth token.");
      }
      if (!candidateId) {
        throw new Error("Candidate ID is missing.");
      }

      const normalizedBase = String(apiBase || "").replace(/\/$/, "");
      const payload = {
        id: candidateId,
        candidate_id: candidateId,
        ...profilePatch,
      };
      const endpoints = [
        `${normalizedBase}/api/candidates/${candidateId}`,
        `${normalizedBase}/api/candidates/${candidateId}/profile`,
        `${normalizedBase}/candidates/${candidateId}`,
        `${normalizedBase}/candidates/${candidateId}/profile`,
      ];
      const requestBodies = [
        payload,
        { candidate: payload },
        { profile: payload },
        { id: candidateId, candidate: payload },
        { id: candidateId, profile: payload },
      ];

      let lastError = null;

      for (const endpoint of endpoints) {
        for (const method of ["PATCH", "PUT"]) {
          for (const body of requestBodies) {
            try {
              const { res, data } = await requestJson({
                url: endpoint,
                method,
                token: authToken,
                body,
              });

              if (res.ok) {
                return data?.candidate || data?.data || data?.item || payload;
              }

              lastError = new Error(
                data?.error ||
                  data?.message ||
                  `Failed to update candidate (${res.status})`
              );
            } catch (error) {
              lastError = error;
            }
          }
        }
      }

      throw lastError || new Error("Failed to update candidate.");
    },
    [apiBase]
  );

  const finalizeLinkCandidateInBackground = useCallback(
    async ({
      authToken,
      candidate,
      companyName,
      companyAiData,
      preferencesJson,
      source,
      notes,
    }) => {
      const candidateId = candidate?.candidate_id ?? candidate?.id ?? null;
      if (!authToken || !candidateId || !companyName) return;

      try {
        const finalCompany = await createCompanyIfMissing(authToken, companyName, {
          ...companyAiData,
          source,
          notes,
          interactive_import: true,
        });
        if (!finalCompany?.id) return;

        const finalCompanyName = finalCompany.name || companyName;
        const nextPreferences = {
          ...(preferencesJson || {}),
          company_client: finalCompanyName,
          company_client_id: finalCompany.id ?? null,
          company_client_logo: finalCompany.logo || "",
          current_company:
            finalCompanyName || preferencesJson?.current_company || "",
          currentCompany:
            finalCompanyName || preferencesJson?.currentCompany || "",
        };

        await updateCandidateProfile(authToken, candidateId, {
          lead_id: finalCompany.id ?? null,
          leadId: finalCompany.id ?? null,
          company_client: finalCompanyName,
          company_client_id: finalCompany.id ?? null,
          company_client_logo: finalCompany.logo || "",
          preferences_json: JSON.stringify(nextPreferences),
        });
      } catch (error) {
        console.error(
          "AddCandidateDialog background link finalization failed:",
          error
        );
      }
    },
    [createCompanyIfMissing, updateCandidateProfile]
  );

  const completeCandidateFlow = useCallback(
    async (candidate, options = {}) => {
      if (!candidate?.id && !candidate?.candidate_id) {
        throw new Error("Candidate could not be opened.");
      }

      if (typeof onCreated === "function") {
        try {
          await callCreatedCallback(
            onCreated,
            candidate,
            options?.callbackContext || {}
          );
        } catch (callbackError) {
          if (requireOnCreatedSuccess) {
            throw callbackError;
          }
          console.error("AddCandidateDialog onCreated callback failed:", callbackError);
        }
      }

      clearOverviewDraft();

      const shouldShowInlineToast =
        Boolean(options?.toastMessage) &&
        (!openProfileOnComplete || !options?.redirectState?.actionToast);

      if (shouldShowInlineToast) {
        setToast({
          open: true,
          type: options.toastType || "info",
          msg: options.toastMessage,
        });
        await new Promise((resolve) => setTimeout(resolve, 900));
      }

      if (options?.redirectToActivities) {
        await closeDialogBeforeNavigation();
        redirectToActivities(candidate, options?.activityMessage);
        return;
      }

      if (options?.skipNavigation) return;

      if (!openProfileOnComplete) {
        onClose?.();
        return;
      }

      await closeDialogBeforeNavigation();
      redirectToCreatedCandidate(candidate, options?.redirectState);
    },
    [
      closeDialogBeforeNavigation,
      onClose,
      onCreated,
      openProfileOnComplete,
      redirectToActivities,
      redirectToCreatedCandidate,
      requireOnCreatedSuccess,
    ]
  );

  const handleSubmit = async (options = {}) => {
    // A recruiter can either use the automatic resume flow or review the
    // extracted form and click Add Candidate themselves. Both are resume
    // imports and must hand off to Activities immediately on submission.
    const autoCreateFromUpload =
      options?.autoCreateFromUpload === true ||
      (resumeFile instanceof File && candidateImportMode === "resume");
    const approvedCompanies = Array.from(
      new Set(
        (options?.primaryCompanyNames || selectedPrimaryCompanies || [])
          .map((companyName) => normalizeCompanyName(companyName))
          .filter(Boolean)
      )
    );
    const approvedPrimaryCompany = approvedCompanies[0] || "";
    const selectedJobStage =
      String(form.stage || DEFAULT_FORM.stage || "New").trim() || "New";
    const callbackContext = showJobStageField
      ? { jobStage: selectedJobStage }
      : {};
    if (shouldRestrictToExistingCandidate && !selectedExistingCandidate?.id) {
      setToast({
        open: true,
        type: "error",
        msg: "Select an existing candidate to add to this job.",
      });
      return;
    }
    if (selectedExistingCandidate?.id) {
      try {
        setSaving(true);
        await completeCandidateFlow(selectedExistingCandidate, {
          callbackContext,
        });
      } catch (e) {
        setToast({
          open: true,
          type: "error",
          msg: e?.message || "Failed to use the selected candidate",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    const validationErrors = buildCandidateValidationErrors("all", form);
    if (Object.keys(validationErrors).length) {
      // Resume parsing is intentionally autonomous: a resume with a usable
      // name should create and open the candidate profile even if the parser
      // could not confidently extract every manual-entry field (for example,
      // Location). The profile can then be completed from its normal page.
      const hasCandidateName = Boolean(String(form.full_name || "").trim());
      if (!autoCreateFromUpload || !hasCandidateName) {
        setErrors(validationErrors);
        if (autoCreateFromUpload) {
          setUploadAutoCreateStage("idle");
          setToast({
            open: true,
            type: "info",
            msg: "Couldn't identify a candidate name from the resume. Please review the parsed details.",
          });
          setScreen("overview");
        }
        return;
      }

      setErrors({});
    }

    const authToken = getToken();
    if (!authToken) {
      setToast({ open: true, type: "error", msg: "No auth token found. Please login again." });
      return;
    }

    let redirectedDuringResumeSubmit = false;
    try {
      setSaving(true);
      if (autoCreateFromUpload) {
        writeCandidateImportWork({
          status: "creating",
          startedAt: new Date().toISOString(),
          message: "Creating candidate from resume. Progress is available in Activities.",
        });
        // Move the recruiter to Activities immediately. The request continues
        // in the background and the server-created CV activity appears in the
        // In Progress column as soon as the candidate is saved.
        await closeDialogBeforeNavigation();
        redirectToActivities({}, "Creating candidate from resume. Progress is available in Activities.");
        redirectedDuringResumeSubmit = true;
      }
      const experienceItems = normalizeAiArray(resumeAiData.experience_items);
      const educationItems = normalizeAiArray(resumeAiData.education_items);
      const inferredCurrentCompany = inferCurrentCompanyFromExperienceItems(experienceItems);
      const pastCompanies = inferPastCompaniesFromExperienceItems(experienceItems);

      const companyName = normalizeCompanyName(
        approvedPrimaryCompany ||
          form.company_client ||
          companyInputValue ||
          selectedCompany?.name ||
          inferredCurrentCompany ||
          ""
      );
      const companyAiData = mergeResumeAiData(resumeAiData, {
        company_client: companyName,
        current_company: companyName,
      });
      const shouldFinalizeLinkInBackground =
        startedFromLinkFlow &&
        !!companyName &&
        !selectedCompany?.id;
      // Resume imports must not create experience companies in the dialog.
      // The background CV workflow publishes each employer to Needs Review,
      // then creates and researches it only after recruiter approval.
      const finalCompany = !autoCreateFromUpload && companyName && !approvedPrimaryCompany
        ? shouldFinalizeLinkInBackground
          ? null
          : await createCompanyIfMissing(authToken, companyName, {
              ...companyAiData,
              source: form.source || "Candidate",
              notes: `Created from candidate import by ${ownerName}`,
              // Primary-company approval must not depend on a profile
              // backfill succeeding. Reuse an existing company immediately,
              // or create the minimal company record; the candidate endpoint
              // then queues its selected company for review with the candidate
              // context attached.
              skipProfileBackfill: Boolean(approvedPrimaryCompany),
              interactive_import: approvedPrimaryCompany
                ? false
                : hasCompanyProfileData(companyAiData),
            })
        : null;
      const finalCompanyClient = finalCompany?.name || companyName || "";
      const finalCompanyClientId = finalCompany?.id ?? null;
      const finalCompanyClientLogo = finalCompany?.logo || "";

      setForm((p) => ({
        ...p,
        company_client: finalCompanyClient,
        company_client_id: finalCompanyClientId,
        company_client_logo: finalCompanyClientLogo,
      }));
      setCompanyInputValue(finalCompanyClient);

      const fullNameTrimmed = String(form.full_name || "").trim();
      const cleanPhone = String(form.phone || "").trim();
      const safeLinkedin = safeUrl(form.linkedin_url) || "";
      const safeWebsiteUrl = safeUrl(form.website_url || form.website) || "";
      const safeOtherUrl = safeUrl(form.other_url) || "";
      const websiteValue = deriveWebsiteValue(form.website, form.website_url);
      const skillsArray = cleanSkillsTokens(splitCsvToArray(form.skills));
      const normalizedLocation = normalizeLocationValueForSave(form.location);
      const locationParts = splitLocationValue(normalizedLocation);
      const extraEmailsArray = splitCsvToArray(form.extra_emails);
      const extraPhonesArray = splitCsvToArray(form.extra_phones);
      const extraWebsitesArray = splitCsvToArray(form.extra_websites);
      const prefJobTypes = splitCsvToArray(form.job_types);
      const prefWorkModes = splitCsvToArray(form.work_modes);
      const prefLocations = normalizeLocationValuesForSave(form.preferred_locations);
      const prefTimeline = String(form.timeline || "").trim();
      const prefCompensation = String(form.compensation || "").trim();
      const prefInterviewAvailability = String(form.interview_availability || "").trim();
      const prefJobType = String(form.preferred_job_type || "").trim();
      const prefWorkMode = String(form.preferred_work_mode || "").trim();
      const prefSalaryRange = String(form.salary_range || "").trim();
      const prefAvailability = String(form.availability || "").trim();
      const whyLooking = String(form.why_looking || "").trim();
      const socialLinks = buildSocialLinks({
        linkedin_url: safeLinkedin,
        website_url: safeWebsiteUrl,
        other_url: safeOtherUrl,
      });
      let assignedTeam = normalizeTeamMemberName(
        form.assigned_team_member || form.team_name || ""
      );
      if (assignedTeam) {
        assignedTeam = await ensureAssignedTeamMemberValue(assignedTeam, authToken);
        setAssignedTeamMemberValue(assignedTeam);
      }
      const splitNames = splitName(fullNameTrimmed);

      const documentsJson = {
        links: {
          linkedin_url: safeLinkedin,
          website_url: safeWebsiteUrl,
          website: websiteValue,
          other_url: safeOtherUrl,
          social_links: socialLinks,
        },
        files: {
          resume_name: resumeFile?.name || "",
          avatar_name: avatarFile?.name || "",
        },
        resume_text: parsedResumeTextRef.current || "",
      };

      const preferencesJson = {
        work_authorization: String(form.work_authorization || form.work_auhtorization || "").trim(),
        source: String(form.source || "").trim(),
        company_client: finalCompanyClient,
        company_client_id: finalCompanyClientId,
        company_client_logo: finalCompanyClientLogo,
        assigned_team_member: assignedTeam,
        notes: String(form.notes || "").trim(),
        skills: skillsArray,
        first_name: splitNames.first || "",
        last_name: splitNames.last || "",
        city: locationParts.city || "",
        state: locationParts.state || "",
        address: String(form.address || "").trim(),
        whyLooking,
        jobTypes: prefJobTypes,
        workModes: prefWorkModes,
        timeline: prefTimeline,
        compensation: prefCompensation,
        interviewAvailability: prefInterviewAvailability,
        jobType: prefJobType,
        workMode: prefWorkMode,
        salaryRange: prefSalaryRange,
        availability: prefAvailability,
        locations: prefLocations,
        social_links: socialLinks,
        extra_emails: extraEmailsArray,
        extra_phones: extraPhonesArray,
        extra_websites: extraWebsitesArray,
        website: websiteValue,
        website_url: safeWebsiteUrl,
        linkedin_url: safeLinkedin,
        other_url: safeOtherUrl,
        current_company: finalCompanyClient || inferredCurrentCompany,
        currentCompany: finalCompanyClient || inferredCurrentCompany,
        past_companies: pastCompanies,
        pastCompanies,
      };

      const payload = {
        job_id: String(form.job_id || "").trim(),
        lead_id: finalCompanyClientId,
        first_name: splitNames.first || "",
        last_name: splitNames.last || "",
        full_name: fullNameTrimmed,
        email: String(form.email || "").trim() || "",
        phone: cleanPhone || "",
        phone_number: cleanPhone || "",
        title: String(form.title || "").trim() || "",
        location: normalizedLocation || "",
        city: locationParts.city || "",
        state: locationParts.state || "",
        address: String(form.address || "").trim(),
        owner: ownerName || "",
        status: String(form.status || "New").trim(),
        stage: String(form.stage || "Applied").trim(),

        resume_url: "",
        // The resume was parsed by the dedicated upload step before this
        // candidate is saved. Tell the server not to repeat that same AI
        // extraction after the upload succeeds.
        resume_was_parsed_client_side: Boolean(
          resumeFile instanceof File && resumeAiParseCompleteRef.current
        ),
        documents_json: JSON.stringify(documentsJson),
        experience_items: JSON.stringify(experienceItems),
        education_items: JSON.stringify(educationItems),
        preferences_json: JSON.stringify(preferencesJson),

        pref_job_type: prefJobType,
        pref_job_types: prefJobTypes,
        pref_work_mode: prefWorkMode,
        pref_work_modes: prefWorkModes,
        pref_salary_range: prefSalaryRange,
        pref_compensation: prefCompensation,
        pref_locations: prefLocations,
        pref_availability: prefAvailability,
        pref_timeline: prefTimeline,
        pref_why_looking: whyLooking,
        pref_interview_availability: prefInterviewAvailability,

        website_url: safeWebsiteUrl,
        website: websiteValue,
        linkedin_url: safeLinkedin,
        social_links: socialLinks,
        socialLinks: socialLinks,
        social_links_text: socialLinks.join(", "),
        socialLinksText: socialLinks.join(", "),
        extra_emails: extraEmailsArray,
        extraEmails: extraEmailsArray,
        extra_phones: extraPhonesArray,
        extraPhones: extraPhonesArray,
        extra_websites: extraWebsitesArray,
        extraWebsites: extraWebsitesArray,
        skills: JSON.stringify(skillsArray),
        work_authorization: String(form.work_authorization || form.work_auhtorization || "").trim(),
        work_auhtorization: String(form.work_authorization || form.work_auhtorization || "").trim(),
        source: String(form.source || "").trim(),
        whyLooking,
        jobTypes: prefJobTypes,
        workModes: prefWorkModes,
        timeline: prefTimeline,
        compensation: prefCompensation,
        interviewAvailability: prefInterviewAvailability,
        jobType: prefJobType,
        workMode: prefWorkMode,
        salaryRange: prefSalaryRange,
        availability: prefAvailability,
        locations: prefLocations,
        team_name: assignedTeam,
        assigned_team_member: assignedTeam,
        company_client: finalCompanyClient,
        company_client_id: finalCompanyClientId,
        company_client_logo: finalCompanyClientLogo,
        other_url: safeOtherUrl,
        notes: String(form.notes || "").trim(),

        // AI-extracted company fields for backend company creation
        company_link: companyAiData.company_link || "",
        career_site: companyAiData.career_site || "",
        career_website: companyAiData.career_site || "",
        company_career_url: companyAiData.career_site || "",
        company_career_website: companyAiData.career_site || "",
        company_website:
          getPrimaryCompanyWebsite({
            company_website: companyAiData.company_website,
            company_link: companyAiData.company_link,
            career_site: companyAiData.career_site,
          }) || "",
        company_url:
          getPrimaryCompanyWebsite({
            company_website: companyAiData.company_website,
            company_link: companyAiData.company_link,
            career_site: companyAiData.career_site,
          }) || "",
        company_location: normalizeLocationValueForSave(companyAiData.company_location || ""),
        company_description: companyAiData.company_description || "",
        full_company_description: companyAiData.full_company_description || "",
        elevator_pitch: companyAiData.elevator_pitch || "",
        mission_and_vision: companyAiData.mission_and_vision || "",
        key_products_services: companyAiData.key_products_services || "",
        unique_selling_points: companyAiData.unique_selling_points || "",
        employee_perks: companyAiData.employee_perks || "",
        achievements_awards: companyAiData.achievements_awards || "",
        industry: companyAiData.industry || companyAiData.industry_sector || "",
        industry_sector: companyAiData.industry_sector || companyAiData.industry || "",
        size_range: companyAiData.size_range || companyAiData.company_size || "",
        company_size: companyAiData.company_size || companyAiData.size_range || "",
        founded_year: companyAiData.founded_year || "",
        ownership_type: companyAiData.ownership_type || "",
        parent_company: companyAiData.parent_company || "",
        parent_company_subsidiaries: companyAiData.parent_company || "",
        work_policies: companyAiData.work_policies || "",
        recruitment_tools: companyAiData.recruitment_tools || [],
        job_boards: companyAiData.job_boards || [],
        job_board_rating: companyAiData.job_board_rating || "",
        company_email: companyAiData.company_email || "",
        company_phone: companyAiData.company_phone || "",
        primary_company_name: approvedPrimaryCompany,
        selected_company_names: approvedCompanies,
      };

      const data = await postCandidate(authToken, payload, resumeFile, avatarFile);
      const created = data?.candidate || data?.data?.candidate || data?.data || data;
      const duplicateCandidate = data?.duplicate === true || data?.created === false;

      const newCandidate = duplicateCandidate
        ? created || null
        : {
            ...payload,
            ...created,
            id: created?.id ?? null,
            resume_url: created?.resume_url || "",
            avatar_url: created?.avatar_url || "",
          };

      if (!newCandidate?.id && !newCandidate?.candidate_id) {
        throw new Error(data?.message || "Candidate could not be opened.");
      }

      // Keep the candidate import visible as active work on the Activities
      // board while the server parses the uploaded resume.
      if (autoCreateFromUpload) {
        const resumeParseActivity = data?.resume_parse_activity || data?.data?.resume_parse_activity || null;
        writeCandidateImportWork({
          status: "creating",
          startedAt: new Date().toISOString(),
          candidateId: newCandidate?.candidate_id ?? newCandidate?.id ?? null,
          activityId: resumeParseActivity?.id ?? null,
          message: "Candidate created. Resume processing is in progress.",
        });
      }

      if (pendingResumeBackgroundParseRef.current && !duplicateCandidate) {
        pendingResumeBackgroundParseRef.current = false;
      }

      const shouldRunLinkBackgroundTask =
        shouldFinalizeLinkInBackground && !duplicateCandidate;

      if (shouldRunLinkBackgroundTask) {
        void finalizeLinkCandidateInBackground({
          authToken,
          candidate: newCandidate,
          companyName,
          companyAiData,
          preferencesJson,
          source: form.source || "Candidate",
          notes: `Created from candidate import by ${ownerName}`,
        });
      }

      await completeCandidateFlow(newCandidate, {
        callbackContext,
        redirectToActivities: autoCreateFromUpload && !redirectedDuringResumeSubmit,
        skipNavigation: redirectedDuringResumeSubmit,
        activityMessage: "Candidate created. Resume processing is in progress.",
        toastMessage: duplicateCandidate
          ? data?.message || "Candidate already exists. Opening profile."
          : autoCreateFromUpload
          ? "Candidate created. Resume processing is in progress."
          : shouldRunLinkBackgroundTask
          ? LINK_BACKGROUND_MESSAGE
          : "",
        toastType: "info",
        redirectState: shouldRunLinkBackgroundTask
          ? {
              actionToast: {
                message: LINK_BACKGROUND_MESSAGE,
                severity: "info",
              },
            }
          : undefined,
      });
    } catch (e) {
      if (autoCreateFromUpload) {
        setUploadAutoCreateStage("idle");
        setCandidateImportHandoff(false);
        writeCandidateImportWork({
          status: "error",
          message: e?.message || "Failed to create candidate from resume.",
        });
      }
      setToast({ open: true, type: "error", msg: e?.message || "Failed to create candidate" });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  useEffect(() => {
    if (!pendingUploadAutoSubmitRef.current) return;
    if (parsingResume || saving) return;

    pendingUploadAutoSubmitRef.current = false;
    setUploadAutoCreateStage("saving");
    handleSubmitRef.current?.({ autoCreateFromUpload: true });
  }, [form, resumeAiData, parsingResume, saving]);

  const candidateLookupRenderableOptions = useMemo(() => {
    const query = String(candidateLookupInputValue || "").trim();
    const baseOptions = Array.isArray(candidateLookupOptions) ? candidateLookupOptions : [];

    if (shouldRestrictToExistingCandidate || !query) return baseOptions;

    const hasExact = baseOptions.some(
      (option) =>
        normalizeCompanyName(option?.full_name || "").toLowerCase() ===
        normalizeCompanyName(query).toLowerCase()
    );
    if (hasExact) return baseOptions;

    return [
      ...baseOptions,
      {
        id: `create-${query.toLowerCase()}`,
        inputValue: query,
        full_name: query,
        createOption: true,
      },
    ];
  }, [candidateLookupInputValue, candidateLookupOptions, shouldRestrictToExistingCandidate]);

  const renderStart = () => (
    <Card title="Add Candidate" onClose={handleClose} saving={saving}>
      <CandidateEntryOptions
        onStartFromScratch={() => {
          setStartedFromLinkFlow(false);
          setScreen("overview");
        }}
        onLinkChange={handleCandidateStartLinkChange}
        onLinkContinue={handleCandidateStartLinkContinue}
        onPickUploadFile={handlePickResumeFromStart}
        linkValue={candidateLinkInput}
        linkHelperText={candidateLinkSummary}
        linkErrorText={errors?.linkedin_url || errors?.company_client || ""}
        linkLoading={saving || creatingLinkCompany}
        uploadDisabled={saving || parsingResume}
        parsingResume={parsingResume}
      />
    </Card>
  );

  const renderUpload = () => (
    <Card title="Add Candidate - Upload" onClose={handleClose} saving={saving}>
      <UploadCandidateStep
        resumeFile={resumeFile}
        resumeAiData={resumeAiData}
        existingResumeUrl={form.resume_url}
        backgroundTasks={uploadBackgroundTasks}
        backgroundTasksTitle={
          uploadAutoCreateStage === "saving"
            ? "Resume parsed. These follow-up tasks are still running:"
            : "Resume parsed. The next tasks are queued to run:"
        }
        backgroundTasksVisible={showUploadBackgroundTasks}
        backgroundTasksNote={
          uploadNeedsCompanyTableEntry ? COMPANY_TABLE_VIEW_MESSAGE : ""
        }
        parsingResume={parsingResume}
        saving={saving}
        onPickResume={handlePickResume}
        onClearResume={clearResume}
      />

      <Box sx={{ px: 4, pb: 4, pt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PillButton
            variant="outlined"
            onClick={() => {
              setUploadAutoCreateStage("idle");
              setScreen("start");
            }}
            disabled={saving || parsingResume}
            sx={{
              borderColor: SOFT_BORDER,
              color: PURPLE,
              bgcolor: "#fff",
            }}
          >
            Back
          </PillButton>
        </Stack>
      </Box>
    </Card>
  );

  const renderLink = () => (
    <Card title="Add Candidate - Link" onClose={handleClose} saving={saving}>
      <UseLinkCandidateStep
        linkValue={candidateLinkInput}
        companyName={companyInputValue || form.company_client || ""}
        source={form.source}
        errors={errors}
        sourceOptions={SOURCE_OPTIONS}
        onCompanyChange={handleLinkCompanyChange}
        onLinkChange={handleCandidateStartLinkChange}
        onSourceChange={setField("source")}
      />

      <Footer
        leftText="Back"
        onLeft={() => setScreen("start")}
        rightText="Continue"
        onRight={handleContinueFromLink}
        saving={saving || creatingLinkCompany}
        parsingResume={parsingResume}
      />
    </Card>
  );

  const renderOverview = () => (
    <Card title="Add Candidate" onClose={handleClose} saving={saving}>
      <StartFromScratchCandidateStep
        firstName={firstName}
        lastName={lastName}
        form={form}
        errors={errors}
        candidateLookupValue={selectedExistingCandidate}
        candidateLookupInputValue={candidateLookupInputValue}
        candidateLookupOptions={candidateLookupRenderableOptions}
        candidateLookupLoading={candidateLookupLoading}
        candidateLookupHelperText={candidateLookupHelperText}
        onFirstChange={setFirst}
        onLastChange={setLast}
        onCandidateLookupChange={handleCandidateLookupChange}
        onCandidateLookupInputChange={handleCandidateLookupInputChange}
        onFieldChange={setField}
        existingCandidateOnly={shouldRestrictToExistingCandidate}
        showCandidateLookupField={showCandidateLookupField}
        showStageField={shouldShowOverviewStageField || requiresJobAndStageSelection}
        stageOptions={
          normalizedJobStageOptions.length ? normalizedJobStageOptions : JOB_MATCH_STAGE_OPTIONS
        }
        showJobField={requiresJobAndStageSelection}
        jobOptions={jobs}
        jobsLoading={jobsLoading}
        statusOptions={CANDIDATE_STATUS_OPTIONS}
        preferredJobTypeOptions={PREFERRED_JOB_TYPE_OPTIONS}
        availabilityOptions={AVAILABILITY_OPTIONS}
      />

      {shouldRestrictToExistingCandidate ? (
        <Footer
          leftText="Cancel"
          onLeft={handleClose}
          rightText={saving ? "Adding..." : existingCandidateSubmitLabel}
          onRight={handleSubmit}
          saving={saving}
          parsingResume={parsingResume}
        />
      ) : (
        <OverviewFooter
          onBack={() => setScreen("start")}
          onNext={() => {
            if (!validateOverview()) return;
            setScreen("professional");
          }}
          saving={saving}
          parsingResume={parsingResume}
        />
      )}
    </Card>
  );

  const renderProfessional = () => (
    <Card title="Add Candidate" onClose={handleClose} saving={saving}>
      <Box sx={{ p: 4 }}>
        <Section>Professional Information</Section>

        <Box sx={{ mt: 1.25 }}>
          <Stack spacing={1.25}>
            {errors?._form ? <Alert severity="error">{errors._form}</Alert> : null}

            <Box>
              <LabelText>Upload Resume (Optional)</LabelText>
              <UploadCandidateStep
                resumeFile={resumeFile}
                resumeAiData={resumeAiData}
                existingResumeUrl={form.resume_url}
                parsingResume={parsingResume}
                saving={saving}
                onPickResume={handlePickResume}
                onClearResume={clearResume}
              />
            </Box>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.6 }}
              >
                <LabelText>Company (Client) (Optional)</LabelText>
                <Button
                  size="small"
                  onClick={fetchCompanies}
                  disabled={companiesLoading}
                  sx={{ textTransform: "none", fontWeight: 900, color: PURPLE }}
                >
                  Refresh
                </Button>
              </Stack>

              <Autocomplete
                freeSolo
                openOnFocus
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                options={companies}
                loading={companiesLoading}
                value={selectedCompany}
                inputValue={companyInputValue}
                onOpen={() => {
                  if (!companies.length && !companiesLoading) fetchCompanies();
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "reset") {
                    setCompanyInputValue(selectedCompany?.name || "");
                    return;
                  }

                  if (selectedExistingCandidate?.id) {
                    clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
                  }

                  const nextInput = newInputValue || "";
                  setCompanyInputValue(nextInput);

                  if (reason === "clear") {
                    setForm((p) => ({
                      ...p,
                      company_client: "",
                      company_client_id: null,
                      company_client_logo: "",
                    }));
                    return;
                  }

                  if (reason === "input") {
                    const typed = normalizeCompanyName(nextInput);
                    const exact = companies.find(
                      (c) =>
                        c.name.toLowerCase() === typed.toLowerCase() ||
                        String(c.id).toLowerCase() === typed.toLowerCase()
                    );

                    setForm((p) => ({
                      ...p,
                      company_client: exact?.name || typed,
                      company_client_id: exact?.id ?? null,
                      company_client_logo: exact?.logo || "",
                      ...(exact?.website
                        ? {
                            website_url: exact.website,
                            website: deriveWebsiteValue(p.website, exact.website) || p.website || "",
                          }
                        : {}),
                    }));

                    setErrors((p) => {
                      const next = { ...p };
                      delete next.company_client;
                      return next;
                    });
                  }
                }}
                onChange={(_, newValue) => {
                  if (selectedExistingCandidate?.id) {
                    clearSelectedExistingCandidate(form.full_name || joinName(firstName, lastName));
                  }

                  const resolved = resolveCompanyValue(newValue, companies);

                  setForm((p) => ({
                    ...p,
                    company_client: resolved.company_client,
                    company_client_id: resolved.company_client_id,
                    company_client_logo: resolved.company_client_logo,
                    ...(resolved.company_website
                      ? {
                          website_url: resolved.company_website,
                          website:
                            deriveWebsiteValue(p.website, resolved.company_website) || p.website || "",
                        }
                      : {}),
                  }));

                  setCompanyInputValue(resolved.company_client || "");

                  setErrors((p) => {
                    const next = { ...p };
                    delete next.company_client;
                    return next;
                  });
                }}
                filterOptions={(options, params) => {
                  const inputValue = normalizeCompanyName(params.inputValue).toLowerCase();

                  const filtered = options.filter((option) => {
                    const byName = option.name.toLowerCase().includes(inputValue);
                    const byId = String(option.id || "").toLowerCase().includes(inputValue);
                    return !inputValue || byName || byId;
                  });

                  const base = dedupeAutocompleteOptions(companyFilter(filtered, {
                    ...params,
                    inputValue: "",
                  }));

                  const exists = options.some(
                    (option) =>
                      option.name.toLowerCase() === inputValue ||
                      String(option.id || "").toLowerCase() === inputValue
                  );

                  if (inputValue && !exists) {
                    base.push({
                      inputValue: params.inputValue,
                      name: params.inputValue,
                      id: null,
                      logo: "",
                    });
                  }

                  return dedupeAutocompleteOptions(base, (option) =>
                    normalizeAutocompleteText(option?.name || option?.inputValue || option?.id || "")
                  );
                }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option?.inputValue) return option.inputValue;
                  return String(option?.name || "");
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?.id ?? null;
                  const valueId = value?.id ?? null;

                  if (optionId != null && valueId != null) {
                    return String(optionId) === String(valueId);
                  }

                  return (
                    normalizeCompanyName(option?.name || option?.inputValue || "").toLowerCase() ===
                    normalizeCompanyName(value?.name || value?.inputValue || "").toLowerCase()
                  );
                }}
                renderOption={(props, option) => {
                  const isAdd = !!option?.inputValue && option?.id == null;

                  return (
                    <Box
                      component="li"
                      {...props}
                      key={`${option?.id ?? "new"}-${option?.name ?? option?.inputValue ?? ""}`}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%" }}>
                        <Avatar
                          src={!isAdd ? option?.logo || "" : ""}
                          variant="rounded"
                          imgProps={{
                            onError: (e) => {
                              e.currentTarget.style.display = "none";
                            },
                          }}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            "& .MuiAvatar-img": {
                              objectFit: "contain",
                              padding: "3px",
                            },
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 15, color: PURPLE }} />
                        </Avatar>

                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#111827",
                          }}
                        >
                          {isAdd ? option.inputValue : <CompanyOptionLabel company={option} />}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search company name or ID"
                    error={!!errors.company_client}
                    helperText={
                      errors.company_client ||
                      (form.company_client_id
                        ? ""
                        : companiesLoading
                        ? "Loading companies..."
                        : companies.length
                        ? `${companies.length} companies loaded`
                        : companiesDebug || "No companies loaded from API")
                    }
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Avatar
                            src={selectedCompany?.logo || form.company_client_logo || ""}
                            variant="rounded"
                            imgProps={{
                              onError: (e) => {
                                e.currentTarget.style.display = "none";
                              },
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              bgcolor: "#FFFFFF",
                              border: `1px solid ${SOFT_BORDER}`,
                              "& .MuiAvatar-img": {
                                objectFit: "contain",
                                padding: "2px",
                                backgroundColor: "#fff",
                              },
                            }}
                          >
                            <BusinessIcon sx={{ fontSize: 13, color: PURPLE }} />
                          </Avatar>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {companiesLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {!companiesLoading && !companies.length && (
                <Typography sx={{ fontSize: 11, color: "#DC2626", mt: 0.75 }}>
                  Debug: {companiesDebug || "No companies returned"}
                </Typography>
              )}
            </Box>

            <Box>
              <LabelText>LinkedIn Profile URL (Optional)</LabelText>
              <TextField
                value={form.linkedin_url}
                onChange={setField("linkedin_url")}
                size="small"
                fullWidth
                error={!!errors.linkedin_url}
                helperText={errors.linkedin_url || ""}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <LabelText>Website (Optional)</LabelText>
              <TextField
                value={form.website_url}
                onChange={setField("website_url")}
                size="small"
                fullWidth
                error={!!errors.website_url}
                helperText={errors.website_url || ""}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <LabelText>Other (Optional)</LabelText>
              <TextField
                value={form.other_url}
                onChange={setField("other_url")}
                size="small"
                fullWidth
                error={!!errors.other_url}
                helperText={errors.other_url || ""}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <LabelText>Skills (Optional)</LabelText>
              <TextField
                value={form.skills}
                onChange={setField("skills")}
                size="small"
                fullWidth
                placeholder="e.g. React, Node, SQL"
                sx={fieldSx}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Work Authorization (Optional)</LabelText>
                <TextField
                  select
                  value={form.work_authorization}
                  onChange={setField("work_authorization")}
                  size="small"
                  fullWidth
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Optional</em>
                  </MenuItem>
                  {WORK_AUTH_OPTIONS.map((w) => (
                    <MenuItem key={w} value={w}>
                      {w}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ flex: 1 }}>
                <LabelText>Source</LabelText>
                <TextField
                  select
                  value={form.source}
                  onChange={setField("source")}
                  size="small"
                  fullWidth
                  error={!!errors.source}
                  helperText={errors.source || ""}
                  sx={fieldSx}
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            <Box>
              <LabelText>Candidate Avatar (Optional)</LabelText>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                <Avatar
                  src={avatarPreview}
                  sx={{ width: 34, height: 34, border: `1px solid ${SOFT_BORDER}` }}
                >
                  <PersonOutlineIcon fontSize="small" />
                </Avatar>

                <PillButton
                  component="label"
                  variant="outlined"
                  disabled={saving}
                  sx={{
                    borderColor: SOFT_BORDER,
                    color: PURPLE,
                    bgcolor: "#fff",
                  }}
                  startIcon={<ImageOutlinedIcon />}
                >
                  Upload Avatar
                  <input hidden type="file" accept="image/*" onChange={handlePickAvatar} />
                </PillButton>

                {avatarFile ? (
                  <>
                    <Chip size="small" label={avatarFile.name} />
                    <Button
                      size="small"
                      onClick={clearAvatar}
                      disabled={saving}
                      sx={{ textTransform: "none", fontWeight: 900, color: PURPLE }}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                    JPG/PNG/WebP
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>

      <Footer
        leftText="Back"
        onLeft={() => setScreen("overview")}
        rightText="Next"
        onRight={() => {
          if (!validateProfessional()) return;
          setScreen("additional");
        }}
        saving={saving}
        parsingResume={parsingResume}
      />
    </Card>
  );

  const renderAdditional = () => (
    <Card title="Add Candidate" onClose={handleClose} saving={saving}>
      <Box sx={{ p: 4 }}>
        <Section>Additional Details</Section>

        <Box sx={{ mt: 1.25 }}>
          <Stack spacing={1.25}>
            {errors?._form ? <Alert severity="error">{errors._form}</Alert> : null}

            <Box>
              <LabelText>Notes (optional)</LabelText>
              <TextField
                value={form.notes}
                onChange={setField("notes")}
                size="small"
                fullWidth
                multiline
                minRows={4}
                sx={textareaFieldSx}
              />
            </Box>

            <Box>
              <LabelText>Address (optional)</LabelText>
              <TextField
                value={form.address}
                onChange={setField("address")}
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="123 Main St, Suite 200"
                sx={textareaFieldSx}
              />
            </Box>

            <Typography sx={{ fontSize: 12, fontWeight: 900, color: PURPLE, pt: 0.5 }}>
              Contact Details
            </Typography>

            <Box>
              <LabelText>Additional Emails (optional)</LabelText>
              <TextField
                value={form.extra_emails}
                onChange={setField("extra_emails")}
                size="small"
                fullWidth
                placeholder="alt@domain.com, second@domain.com"
                sx={fieldSx}
              />
            </Box>

            <Box>
              <LabelText>Additional Phones (optional)</LabelText>
              <TextField
                value={form.extra_phones}
                onChange={setField("extra_phones")}
                size="small"
                fullWidth
                placeholder="+1 555 111 2222, +1 555 333 4444"
                sx={fieldSx}
              />
            </Box>

            <Box>
              <LabelText>Additional Websites (optional)</LabelText>
              <TextField
                value={form.extra_websites}
                onChange={setField("extra_websites")}
                size="small"
                fullWidth
                placeholder="https://portfolio.site, https://github.com/name"
                sx={fieldSx}
              />
            </Box>

            <Typography sx={{ fontSize: 12, fontWeight: 900, color: PURPLE, pt: 0.5 }}>
              Preferences
            </Typography>

            <Box>
              <LabelText>Why Looking (optional)</LabelText>
              <TextField
                value={form.why_looking}
                onChange={setField("why_looking")}
                size="small"
                fullWidth
                multiline
                minRows={3}
                sx={textareaFieldSx}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Preferred Work Modes (optional)</LabelText>
                <TextField
                  value={form.work_modes}
                  onChange={setField("work_modes")}
                  size="small"
                  fullWidth
                  placeholder="Remote, Hybrid"
                  sx={fieldSx}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Primary Job Type (optional)</LabelText>
                <TextField
                  value={form.preferred_job_type}
                  onChange={setField("preferred_job_type")}
                  size="small"
                  fullWidth
                  placeholder="Full-time"
                  sx={fieldSx}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <LabelText>Primary Work Mode (optional)</LabelText>
                <TextField
                  value={form.preferred_work_mode}
                  onChange={setField("preferred_work_mode")}
                  size="small"
                  fullWidth
                  placeholder="Remote"
                  sx={fieldSx}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Salary Range (optional)</LabelText>
                <TextField
                  value={form.salary_range}
                  onChange={setField("salary_range")}
                  size="small"
                  fullWidth
                  placeholder="$120,000 - $150,000"
                  sx={fieldSx}
                />
              </Box>

              <Box sx={{ flex: 1 }} />
            </Stack>

            <Box>
              <LabelText>Preferred Locations (optional)</LabelText>
              <LocationMultiSelectField
                value={normalizeLocationValuesForSave(form.preferred_locations)}
                onChange={(nextValues) =>
                  setForm((prev) => ({
                    ...prev,
                    preferred_locations: normalizeLocationValuesForSave(nextValues).join(" | "),
                  }))
                }
                placeholder="San Francisco, CA | Seattle, WA"
                inputSx={fieldSx}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Timeline (optional)</LabelText>
                <TextField
                  value={form.timeline}
                  onChange={setField("timeline")}
                  size="small"
                  fullWidth
                  placeholder="Immediately"
                  sx={fieldSx}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <LabelText>Compensation (optional)</LabelText>
                <TextField
                  value={form.compensation}
                  onChange={setField("compensation")}
                  size="small"
                  fullWidth
                  placeholder="Base + bonus"
                  sx={fieldSx}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Box sx={{ flex: 1 }}>
                <LabelText>Interview Availability (optional)</LabelText>
                <TextField
                  value={form.interview_availability}
                  onChange={setField("interview_availability")}
                  size="small"
                  fullWidth
                  placeholder="Weekday mornings"
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ flex: 1 }} />
            </Stack>

            <Box>
              <LabelText>Assign Team Member (optional)</LabelText>
              <Autocomplete
                freeSolo
                openOnFocus
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                options={assignTeamOptions}
                loading={assignTeamLoading}
                value={selectedTeamMember}
                inputValue={form.assigned_team_member}
                onOpen={() => {
                  if (selectedCompanyId) {
                    if (!companyContacts.length && !companyContactsLoading) {
                      loadCompanyContacts(selectedCompanyId);
                    }
                    return;
                  }
                  if (!teamMembers.length && !teamMembersLoading) fetchTeamMembersList();
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "reset") return;
                  if (reason === "clear") {
                    setAssignedTeamMemberValue("");
                    return;
                  }
                  setAssignedTeamMemberValue(newInputValue || "");
                }}
                onChange={(_, newValue) => {
                  if (typeof newValue === "string") {
                    setAssignedTeamMemberValue(newValue);
                    return;
                  }
                  setAssignedTeamMemberValue(
                    newValue?.inputValue || newValue?.name || ""
                  );
                }}
                filterOptions={(options, params) => {
                  const inputValue = normalizeTeamMemberName(params.inputValue).toLowerCase();
                  const filtered = options.filter(
                    (option) => !inputValue || option.name.toLowerCase().includes(inputValue)
                  );
                  const base = dedupeAutocompleteOptions(teamMemberFilter(filtered, {
                    ...params,
                    inputValue: "",
                  }));
                  const exists = options.some(
                    (option) => option.name.toLowerCase() === inputValue
                  );

                  if (inputValue && !exists) {
                    base.push({
                      id: null,
                      inputValue: params.inputValue,
                      name: params.inputValue,
                    });
                  }

                    return dedupeAutocompleteOptions(base, (option) =>
                      normalizeAutocompleteText(option?.name || option?.inputValue || "")
                    );
                }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option?.inputValue) return option.inputValue;
                  return String(option?.name || "");
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionId = option?.id ?? null;
                  const valueId = value?.id ?? null;

                  if (optionId != null && valueId != null) {
                    return String(optionId) === String(valueId);
                  }

                  return (
                    normalizeTeamMemberName(
                      option?.name || option?.inputValue || ""
                    ).toLowerCase() ===
                    normalizeTeamMemberName(
                      value?.name || value?.inputValue || ""
                    ).toLowerCase()
                  );
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={`${option?.id ?? "new"}-${option?.name ?? option?.inputValue ?? ""}`}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                      {option?.inputValue
                        ? `Create "${option.inputValue}"`
                        : option?.name || ""}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    placeholder={
                      selectedCompanyId
                        ? "Search company contacts or type a name"
                        : "Search or type a team member name"
                    }
                    helperText={
                      selectedCompanyId
                        ? assignTeamLoading
                          ? "Loading company contacts..."
                          : assignTeamOptions.length
                            ? "Search existing company contacts or type another name manually."
                            : assignTeamDebug || "No saved company contacts yet. Type a name manually."
                        : teamMembersLoading
                          ? "Loading team members..."
                          : teamMembers.length
                            ? "Search existing team members or type a new name to create on save."
                            : teamMembersDebug || "No team members yet. Type a name to create one on save."
                    }
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {assignTeamLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Stack>
        </Box>
      </Box>

      <Footer
        leftText="Back"
        onLeft={() => setScreen("professional")}
        rightText={
          saving
            ? "Saving..."
            : selectedExistingCandidate?.id
            ? "Add to Job"
            : "Add Candidate"
        }
        onRight={handleSubmit}
        saving={saving}
        parsingResume={parsingResume}
      />
    </Card>
  );

  const current = () => {
    if (screen === "start") return renderStart();
    if (screen === "upload") return renderUpload();
    if (screen === "link") return renderLink();
    if (screen === "overview") return renderOverview();
    if (screen === "professional") return renderProfessional();
    if (screen === "additional") return renderAdditional();
    return renderStart();
  };

  return (
    <>
      <Dialog
        open={open && !candidateImportHandoff}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 24px)", sm: 520 },
            maxWidth: "520px",
            borderRadius: 4,
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 1, bgcolor: "transparent" }}>{current()}</Box>
      </Dialog>

      <Dialog
        open={primaryCompanyPickerOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 800 }}>Select companies</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: 14, color: "#475467" }}>
            The candidate has been saved. Select work-experience companies to create or update; their Contacts and Leads will be added to Needs Review for approval.
          </Typography>
          <GenericDataTable
              columns={companySelectionColumns}
              rows={extractedResumeCompanies.map((companyName) => ({
                id: companyName,
                company_name: companyName,
                company_status: existingCompanyIdsByName.has(normalizeCompanyName(companyName).toLowerCase())
                  ? "existing"
                  : companiesLoading
                  ? "checking"
                  : "new",
              }))}
              defaultOrderBy="company_name"
              includeSelectionColumn={true}
              selectedIds={selectedPrimaryCompanies}
              onSelectedIdsChange={setSelectedPrimaryCompanies}
              initialRowsPerPage={10}
              showResultsHeader={false}
              tableContainerSx={{ border: `1px solid ${SOFT_BORDER}`, borderRadius: 2, maxHeight: "44vh" }}
              renderRow={(row) => (
                <>
                  <TableCell><CompanyTableCell row={row} /></TableCell>
                  <TableCell>
                    {row.company_status === "existing" ? (
                      <Chip size="small" label="Already exists" color="success" variant="outlined" />
                    ) : row.company_status === "checking" ? (
                      <Typography sx={{ fontSize: 12, color: "#667085" }}>Checking</Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "#667085" }}>New company</Typography>
                    )}
                  </TableCell>
                </>
              )}
          />
          {companyJobPickerLoading ? (
            <Box sx={{ mt: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475467", mb: 0.8 }}>
                {`Finding opening jobs for ${companyJobScanProgress.companyName || "selected companies"} (${companyJobScanProgress.completed} of ${companyJobScanProgress.total})`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  companyJobScanProgress.total
                    ? (companyJobScanProgress.completed / companyJobScanProgress.total) * 100
                    : 0
                }
                sx={{ height: 8, borderRadius: 99 }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            disabled={!selectedPrimaryCompanies.length || companyJobPickerLoading || saving}
            onClick={() => {
              const approvedCompanies = Array.from(
                new Set(
                  selectedPrimaryCompanies
                    .map((companyName) => normalizeCompanyName(companyName))
                    .filter(Boolean)
                )
              );
              const approvedCompany = approvedCompanies[0] || "";
              if (!approvedCompany) return;
              setForm((currentForm) => ({
                ...currentForm,
                company_client: approvedCompany,
              }));
              setCompanyInputValue(approvedCompany);
              setCandidateImportHandoff(true);
              void handleSubmit({
                autoCreateFromUpload: true,
                primaryCompanyNames: approvedCompanies,
                continueToCompanyJobs: true,
              });
            }}
          >
            {companyJobPickerLoading ? "Finding opening jobs…" : "Approve Selected Companies"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={companyJobPickerOpen}
        onClose={companyJobPickerLoading || companyJobPickerSaving ? () => {} : undefined}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 800 }}>Select opening jobs to add</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2, fontSize: 14, color: "#475467" }}>
            The AI Agent found these openings for the selected companies. Select the openings to add as jobs.
          </Typography>
          {companyJobPickerLoading ? (
            <Stack alignItems="center" sx={{ py: 5 }} spacing={1.5}>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 13, color: "#667085" }}>
                Finding opening jobs…
              </Typography>
            </Stack>
          ) : companyJobRows.length ? (
            <GenericDataTable
              columns={companyJobColumns}
              rows={companyJobRows}
              defaultOrderBy="company_name"
              includeSelectionColumn={true}
              selectedIds={selectedCompanyJobIds}
              onSelectedIdsChange={setSelectedCompanyJobIds}
              initialRowsPerPage={10}
              tableContainerSx={{ border: `1px solid ${SOFT_BORDER}`, borderRadius: 2, maxHeight: "52vh" }}
              renderRow={(row) => (
                <>
                  <TableCell><CompanyTableCell row={row} /></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{row.job_title || "-"}</TableCell>
                  <TableCell>{row.location || "-"}</TableCell>
                  <TableCell>{row.employment_type || "-"}</TableCell>
                  <TableCell>
                    {row.job_url ? (
                      <Box component="a" href={row.job_url} target="_blank" rel="noreferrer" sx={{ color: PURPLE }}>
                        Open link
                      </Box>
                    ) : "-"}
                  </TableCell>
                </>
              )}
            />
          ) : (
            <Typography sx={{ py: 3, color: "#667085" }}>
              No opening jobs were found for the selected companies. You can continue without adding jobs.
            </Typography>
          )}
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, py: 2 }}>
          <Button
            disabled={companyJobPickerSaving}
            onClick={async () => {
              setCompanyJobPickerOpen(false);
              if (createdCandidateForCompanyJobs) {
                await completeCandidateFlow(createdCandidateForCompanyJobs, {
                  toastMessage: "Candidate created. Company research is running in the background.",
                  toastType: "info",
                });
              }
            }}
          >
            Skip jobs
          </Button>
          <Button
            variant="contained"
            disabled={companyJobPickerLoading || companyJobPickerSaving || !selectedCompanyJobIds.length}
            onClick={async () => {
              const authToken = getToken();
              const selectedRows = companyJobRows.filter((row) => selectedCompanyJobIds.includes(String(row.id)));
              setCompanyJobPickerSaving(true);
              try {
                await Promise.all(
                  approvedCompanyRecords.map(async (company) => {
                    const jobs = selectedRows.filter((row) => String(row.company_id) === String(company.id));
                    if (!jobs.length) return;
                    const { res, data } = await requestJson({
                      url: `${apiBase}/api/companies/${encodeURIComponent(String(company.id))}/career-jobs/import`,
                      method: "POST",
                      token: authToken,
                      body: { jobs, import_target: "jobs" },
                    });
                    if (!res.ok) throw new Error(data?.error || "Unable to add selected opening jobs.");
                  })
                );
                setCompanyJobPickerOpen(false);
                if (createdCandidateForCompanyJobs) {
                  await completeCandidateFlow(createdCandidateForCompanyJobs, {
                    toastMessage: "Candidate created and selected opening jobs were added.",
                    toastType: "success",
                  });
                }
              } catch (error) {
                setToast({ open: true, type: "error", msg: error?.message || "Unable to add selected opening jobs." });
              } finally {
                setCompanyJobPickerSaving(false);
              }
            }}
          >
            {companyJobPickerSaving ? "Adding jobs…" : `Add selected jobs (${selectedCompanyJobIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert severity={toast.type} onClose={() => setToast((p) => ({ ...p, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
